import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Map, { Marker, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Search,
  Navigation2,
  Mountain,
  CornerDownRight,
  AlertTriangle,
  Clock,
  Route as RouteIcon,
  Loader2,
  X,
  Save,
  Plus,
  ChevronRight,
  TrendingUp,
  GripVertical,
  LocateFixed,
  MousePointer2,
  ListOrdered,
  ChevronDown,
  ChevronUp,
  Ban,
  RefreshCw,
  Menu,
  ChevronLeft,
  Car,
  Play,
  Square,
  Volume2,
  MapPin,
  Ship,
  Coins,
  Settings2,
  Fuel,
  Thermometer,
  BookOpen,
  Users,
  ClipboardCheck,
  Construction,
  Camera,
  Pause,
  Sun,
  Moon,
  Radar,
  Siren,
  Leaf
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import { VehicleStressIndex, WeatherAlongRoute, CoolingStops, FuelStationFilter } from './RouteFeatures';
import RouteDiary from './RouteDiary';
import RoadConditions from './RoadConditions';
import ConvoyMode from './ConvoyMode';
import PreTripChecklist from './PreTripChecklist';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// MapLibre GL styles with 3D terrain
const MAP_STYLE_DARK = {
  version: 8,
  name: 'Curvio Dark 3D',
  sources: {
    'raster-tiles': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png'
      ],
      tileSize: 256,
      attribution: '&copy; OSM &copy; CARTO'
    },
    'terrainSource': {
      type: 'raster-dem',
      tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
      encoding: 'terrarium',
      tileSize: 256,
      maxzoom: 15,
    }
  },
  layers: [{ id: 'simple-tiles', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 22 }],
  terrain: { source: 'terrainSource', exaggeration: 1.5 },
  sky: { 'sky-color': '#0f172a', 'horizon-color': '#1e293b', 'fog-color': '#0f172a', 'sky-horizon-blend': 0.5, 'horizon-fog-blend': 0.5, 'fog-ground-blend': 0.5 }
};

const MAP_STYLE_LIGHT = {
  version: 8,
  name: 'Curvio Light 3D',
  sources: {
    'raster-tiles': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png'
      ],
      tileSize: 256,
      attribution: '&copy; OSM &copy; CARTO'
    },
    'terrainSource': {
      type: 'raster-dem',
      tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
      encoding: 'terrarium',
      tileSize: 256,
      maxzoom: 15,
    }
  },
  layers: [{ id: 'simple-tiles', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 22 }],
  terrain: { source: 'terrainSource', exaggeration: 1.5 },
  sky: { 'sky-color': '#87CEEB', 'horizon-color': '#B0D4E8', 'fog-color': '#E8F0F8', 'sky-horizon-blend': 0.5, 'horizon-fog-blend': 0.5, 'fog-ground-blend': 0.5 }
};

// Calculate bearing between two [lng, lat] points
const calculateBearing = (start, end) => {
  const startLat = start[1] * Math.PI / 180;
  const startLng = start[0] * Math.PI / 180;
  const endLat = end[1] * Math.PI / 180;
  const endLng = end[0] * Math.PI / 180;
  const dLng = endLng - startLng;
  const y = Math.sin(dLng) * Math.cos(endLat);
  const x = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
};

// Glassmorphism style helpers (theme-aware)
const getGlassPanel = (isDark) => isDark
  ? 'backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl shadow-2xl'
  : 'backdrop-blur-xl bg-white/80 border border-gray-200/60 rounded-2xl shadow-xl';
const getGlassButton = (isDark) => isDark
  ? 'backdrop-blur-md bg-white/10 border border-white/10 hover:bg-white/20 text-white transition-all'
  : 'backdrop-blur-md bg-white/70 border border-gray-200/60 hover:bg-white/90 text-gray-800 transition-all shadow-sm';

// Elevation Profile - Terrain X-Ray™ Design
const ElevationProfile = ({ elevation, warnings, onSelectSteepSection, avoidedSections, onHoverPosition }) => {
  const [selection, setSelection] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const svgRef = useRef(null);

  if (!elevation?.points?.length) return null;

  const points = elevation.points;
  const maxElev = elevation.max;
  const minElev = elevation.min;
  const range = maxElev - minElev || 1;
  const width = 400;
  const height = expanded ? 140 : 90;
  const padding = { top: 8, bottom: 2, left: 0, right: 0 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const gradients = points.map((p, i) => {
    if (i === 0) return 0;
    return ((p[2] - points[i-1][2]) / 100) * 100;
  });

  const pathSegments = points.map((p, i) => ({
    x: padding.left + (i / (points.length - 1)) * chartW,
    y: padding.top + chartH - ((p[2] - minElev) / range) * chartH,
    gradient: gradients[i],
    elevation: p[2]
  }));

  // Detect peaks (local maxima above threshold)
  const peaks = [];
  for (let i = 2; i < points.length - 2; i++) {
    const prev2 = points[i-2][2], prev1 = points[i-1][2], curr = points[i][2], next1 = points[i+1][2], next2 = points[i+2][2];
    if (curr > prev1 && curr > next1 && curr > prev2 && curr > next2 && curr - minElev > range * 0.3) {
      // Check if far enough from last peak
      if (peaks.length === 0 || i - peaks[peaks.length - 1].idx > points.length * 0.08) {
        peaks.push({ idx: i, elev: curr, x: pathSegments[i].x, y: pathSegments[i].y });
      }
    }
  }

  const getGradientColor = (g) => {
    const abs = Math.abs(g);
    if (abs > 12) return '#EF4444';
    if (abs > 8) return '#F97316';
    if (abs > 5) return '#EAB308';
    return '#22C55E';
  };

  const getGradientColorRGB = (g) => {
    const abs = Math.abs(g);
    if (abs > 12) return '239,68,68';
    if (abs > 8) return '249,115,22';
    if (abs > 5) return '234,179,8';
    return '34,197,94';
  };

  const steepSections = [];
  let inSteep = false, steepStart = 0;
  gradients.forEach((g, i) => {
    if (Math.abs(g) > 10 && !inSteep) { steepStart = i; inSteep = true; }
    else if (Math.abs(g) <= 10 && inSteep) { steepSections.push({ start: steepStart, end: i }); inSteep = false; }
  });
  if (inSteep) steepSections.push({ start: steepStart, end: gradients.length - 1 });

  const handleTouch = (e, type) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const touch = e.touches?.[0] || e;
    const x = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
    const idx = Math.round((x / 100) * (points.length - 1));

    if (type === 'hover') {
      setHoverIdx(idx);
      if (onHoverPosition && points[idx]) {
        onHoverPosition({ lat: points[idx][1], lng: points[idx][0], idx, gradient: gradients[idx] });
      }
      return;
    }
    if (type === 'start') {
      setSelection({ start: x, end: x });
      setIsSelecting(true);
    } else if (type === 'move' && isSelecting) {
      setSelection(prev => prev ? { ...prev, end: x } : null);
    } else if (type === 'end' && selection && Math.abs(selection.end - selection.start) > 3) {
      onSelectSteepSection({ startPct: Math.min(selection.start, selection.end), endPct: Math.max(selection.start, selection.end) });
      setIsSelecting(false);
      setSelection(null);
    } else {
      setIsSelecting(false);
      setSelection(null);
      // Single click on steep section → trigger avoidance
      if (hoverIdx !== null && Math.abs(gradients[hoverIdx]) > 8) {
        const section = steepSections.find(s => hoverIdx >= s.start && hoverIdx <= s.end);
        if (section) {
          const startPct = (section.start / points.length) * 100;
          const endPct = (section.end / points.length) * 100;
          onSelectSteepSection({ startPct, endPct });
        }
      }
    }
  };

  // Build area path with layered terrain fill
  const areaPath = `M ${pathSegments[0].x} ${height} ${pathSegments.map(s => `L ${s.x} ${s.y}`).join(' ')} L ${pathSegments[pathSegments.length-1].x} ${height} Z`;
  const linePath = pathSegments.map((s, i) => `${i === 0 ? 'M' : 'L'} ${s.x} ${s.y}`).join(' ');

  // Elevation grid lines
  const gridLines = 4;
  const gridElev = Array.from({ length: gridLines }, (_, i) => minElev + (range / (gridLines - 1)) * i);

  const hoverPt = hoverIdx !== null && pathSegments[hoverIdx];
  const hoverElev = hoverIdx !== null && points[hoverIdx]?.[2];
  const hoverGrad = hoverIdx !== null && gradients[hoverIdx];

  return (
    <div className="relative group">
      {/* Main container with terrain-glass style */}
      <div className="relative overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-transparent">
        {/* Header bar */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-amber-500/30 to-emerald-500/30 flex items-center justify-center">
              <TrendingUp className="w-3 h-3 text-amber-400" />
            </div>
            <span className="text-[10px] sm:text-xs font-semibold text-white/80 tracking-wide uppercase">Terrain X-Ray</span>
            {steepSections.length > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 font-mono font-bold animate-pulse">{steepSections.length} Steil</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-2 text-[9px] sm:text-[10px] font-mono">
              <span className="text-emerald-400/80">↑{Math.round(elevation.total_ascent)}m</span>
              <span className="text-blue-400/80">↓{Math.round(elevation.total_descent)}m</span>
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-white/40 hover:text-white/80 transition-colors"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* Gradient legend - horizontal pills */}
        <div className="flex gap-1 px-3 py-1 text-[8px] sm:text-[9px]">
          {[
            { color: '#22C55E', label: '<5%', bg: 'rgba(34,197,94,0.15)' },
            { color: '#EAB308', label: '5-8%', bg: 'rgba(234,179,8,0.15)' },
            { color: '#F97316', label: '8-12%', bg: 'rgba(249,115,22,0.15)' },
            { color: '#EF4444', label: '>12%', bg: 'rgba(239,68,68,0.15)' },
          ].map(l => (
            <span key={l.label} className="flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium" style={{ background: l.bg, color: l.color }}>
              <span className="w-1 h-1 rounded-full" style={{ background: l.color }}></span>
              {l.label}
            </span>
          ))}
          <span className="ml-auto text-white/30 text-[8px] italic hidden sm:inline">Ziehe um Peaks auszuschließen</span>
        </div>

        {/* Chart area */}
        <div
          className="relative cursor-crosshair select-none touch-none px-1"
          onMouseDown={(e) => handleTouch(e, 'start')}
          onMouseMove={(e) => { handleTouch(e, 'hover'); if (isSelecting) handleTouch(e, 'move'); }}
          onMouseUp={(e) => handleTouch(e, 'end')}
          onMouseLeave={() => { setIsSelecting(false); setSelection(null); setHoverIdx(null); onHoverPosition?.(null); }}
          onTouchStart={(e) => handleTouch(e, 'start')}
          onTouchMove={(e) => handleTouch(e, 'move')}
          onTouchEnd={(e) => handleTouch(e, 'end')}
        >
          <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} className={`w-full ${expanded ? 'h-28 sm:h-36' : 'h-16 sm:h-20'} transition-all duration-300`} preserveAspectRatio="none">
            <defs>
              {/* Terrain gradient - layered earth tones */}
              <linearGradient id="terrainFill" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.35" />
                <stop offset="30%" stopColor="#92400E" stopOpacity="0.2" />
                <stop offset="70%" stopColor="#365314" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#064E3B" stopOpacity="0.05" />
              </linearGradient>
              {/* Glow filter for the line */}
              <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              {/* Peak marker glow */}
              <filter id="peakGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              {/* Scan line pattern */}
              <pattern id="scanlines" width="4" height="4" patternUnits="userSpaceOnUse">
                <line x1="0" y1="0" x2="4" y2="0" stroke="rgba(255,255,255,0.02)" strokeWidth="1" />
              </pattern>
            </defs>

            {/* Subtle grid lines */}
            {gridElev.map((elev, i) => {
              const y = padding.top + chartH - ((elev - minElev) / range) * chartH;
              return (
                <g key={i} style={{ pointerEvents: 'none' }}>
                  <line x1={padding.left} y1={y} x2={width} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" strokeDasharray="2,6" />
                  {expanded && <text x={width - 2} y={y - 2} textAnchor="end" fill="rgba(255,255,255,0.2)" fontSize="6" fontFamily="monospace">{Math.round(elev)}m</text>}
                </g>
              );
            })}

            {/* Avoided sections */}
            {avoidedSections?.map((s, i) => (
              <rect key={i} x={(s.startPct / 100) * width} y="0" width={((s.endPct - s.startPct) / 100) * width} height={height} fill="rgba(239,68,68,0.12)" stroke="rgba(239,68,68,0.4)" strokeWidth="1" strokeDasharray="4,3" rx="2" style={{ pointerEvents: 'none' }} />
            ))}

            {/* Steep section highlights - subtle danger zones */}
            {steepSections.map((s, i) => {
              const x = (s.start / points.length) * chartW + padding.left;
              const w = ((s.end - s.start) / points.length) * chartW;
              return (
                <g key={`steep-${i}`} style={{ pointerEvents: 'none' }}>
                  <rect x={x} y={0} width={w} height={height} fill="rgba(239,68,68,0.06)" />
                  <line x1={x} y1={0} x2={x} y2={height} stroke="rgba(239,68,68,0.2)" strokeWidth="0.5" strokeDasharray="2,4" />
                  <line x1={x + w} y1={0} x2={x + w} y2={height} stroke="rgba(239,68,68,0.2)" strokeWidth="0.5" strokeDasharray="2,4" />
                </g>
              );
            })}

            {/* Highlighted steep section on hover */}
            {hoverIdx !== null && steepSections.map((s, i) => {
              if (hoverIdx >= s.start && hoverIdx <= s.end) {
                const x = (s.start / points.length) * chartW + padding.left;
                const w = ((s.end - s.start) / points.length) * chartW;
                return (
                  <rect key={`steep-hover-${i}`} x={x} y={0} width={w} height={height}
                    fill="rgba(239,68,68,0.20)" stroke="rgba(239,68,68,0.6)" strokeWidth="1.5" rx="2"
                    style={{ pointerEvents: 'none' }} />
                );
              }
              return null;
            })}

            {/* Scanline overlay */}
            <rect x="0" y="0" width={width} height={height} fill="url(#scanlines)" style={{ pointerEvents: 'none' }} />

            {/* Terrain fill area */}
            <path d={areaPath} fill="url(#terrainFill)" style={{ pointerEvents: 'none' }} />

            {/* Gradient-colored segments with glow */}
            {pathSegments.slice(1).map((seg, i) => (
              <line key={i} x1={pathSegments[i].x} y1={pathSegments[i].y} x2={seg.x} y2={seg.y}
                stroke={getGradientColor(seg.gradient)} strokeWidth={expanded ? 2.5 : 2} strokeLinecap="round"
                filter="url(#lineGlow)" opacity="0.9" style={{ pointerEvents: 'none' }} />
            ))}

            {/* Thin bright top line for definition */}
            <path d={linePath} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" style={{ pointerEvents: 'none' }} />

            {/* Peak markers - clickable to avoid */}
            {peaks.map((peak, i) => {
              // Calculate section bounds around peak for avoidance
              const peakPct = (peak.idx / points.length) * 100;
              const sectionRadius = 8; // ±8% around peak
              const isAvoided = avoidedSections?.some(s => peakPct >= s.startPct && peakPct <= s.endPct);
              return (
                <g key={`peak-${i}`} className="cursor-pointer" style={{ pointerEvents: 'all' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isAvoided) {
                      onSelectSteepSection({ startPct: Math.max(0, peakPct - sectionRadius), endPct: Math.min(100, peakPct + sectionRadius) });
                    }
                  }}
                >
                  {/* Hit area (invisible but clickable) */}
                  <circle cx={peak.x} cy={peak.y - 6} r="10" fill="transparent" />
                  {/* Pulsing ring on hover */}
                  <circle cx={peak.x} cy={peak.y - 6} r="8" fill="none" stroke={isAvoided ? 'rgba(139,92,246,0.4)' : 'rgba(239,68,68,0.3)'} strokeWidth="1" opacity="0.6" />
                  {/* Peak triangle */}
                  <polygon
                    points={`${peak.x},${peak.y - 2} ${peak.x - 5},${peak.y - 11} ${peak.x + 5},${peak.y - 11}`}
                    fill={isAvoided ? 'rgba(139,92,246,0.6)' : 'rgba(239,68,68,0.8)'}
                    stroke={isAvoided ? 'rgba(139,92,246,0.8)' : 'rgba(255,255,255,0.5)'}
                    strokeWidth="0.5" filter="url(#peakGlow)"
                  />
                  {/* X mark if avoided */}
                  {isAvoided && (
                    <>
                      <line x1={peak.x - 3} y1={peak.y - 9} x2={peak.x + 3} y2={peak.y - 5} stroke="white" strokeWidth="1.5" />
                      <line x1={peak.x + 3} y1={peak.y - 9} x2={peak.x - 3} y2={peak.y - 5} stroke="white" strokeWidth="1.5" />
                    </>
                  )}
                  {/* Elevation label */}
                  <text x={peak.x} y={peak.y - 13} textAnchor="middle" fill={isAvoided ? 'rgba(139,92,246,0.8)' : 'rgba(239,68,68,0.9)'} fontSize="7" fontWeight="bold" fontFamily="monospace">
                    {Math.round(peak.elev)}m
                  </text>
                  {/* "Tap to avoid" hint on expanded */}
                  {expanded && !isAvoided && (
                    <text x={peak.x} y={peak.y - 19} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="5" fontFamily="sans-serif">
                      ✕ vermeiden
                    </text>
                  )}
                </g>
              );
            })}

            {/* Hover crosshair */}
            {hoverPt && (
              <g>
                <line x1={hoverPt.x} y1={0} x2={hoverPt.x} y2={height} stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" strokeDasharray="2,2" />
                <line x1={0} y1={hoverPt.y} x2={width} y2={hoverPt.y} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" strokeDasharray="2,2" />
                <circle cx={hoverPt.x} cy={hoverPt.y} r="4" fill={getGradientColor(hoverGrad)} stroke="white" strokeWidth="1.5" opacity="0.9" />
                <circle cx={hoverPt.x} cy={hoverPt.y} r="7" fill="none" stroke={getGradientColor(hoverGrad)} strokeWidth="0.5" opacity="0.4" />
              </g>
            )}

            {/* Selection overlay */}
            {selection && (
              <rect x={Math.min(selection.start, selection.end) / 100 * width} y="0"
                width={Math.abs(selection.end - selection.start) / 100 * width} height={height}
                fill="rgba(139,92,246,0.2)" stroke="rgba(139,92,246,0.6)" strokeWidth="1.5" rx="2" />
            )}
          </svg>

          {/* Hover tooltip - floating glass card */}
          {hoverPt && hoverElev !== null && (
            <div
              className="absolute pointer-events-none z-10 transition-all duration-75"
              style={{
                left: `${(hoverPt.x / width) * 100}%`,
                top: expanded ? '-8px' : '-4px',
                transform: `translateX(${(hoverPt.x / width) > 0.7 ? '-100%' : '0'})`,
              }}
            >
              <div className={`backdrop-blur-xl bg-black/70 border rounded-lg px-2.5 py-1.5 shadow-xl ${Math.abs(hoverGrad) > 8 ? 'border-red-500/40' : 'border-white/10'}`}>
                <div className="text-[11px] font-mono font-bold text-white">{Math.round(hoverElev)}m</div>
                <div className="text-[9px] font-mono" style={{ color: getGradientColor(hoverGrad) }}>
                  {hoverGrad > 0 ? '↗' : hoverGrad < 0 ? '↘' : '→'} {Math.abs(hoverGrad).toFixed(1)}%
                </div>
                {Math.abs(hoverGrad) > 8 && (
                  <div className="text-[8px] text-amber-400 mt-0.5 font-medium animate-pulse">
                    Klicke für flachere Route
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Elevation labels */}
          <div className="absolute left-1 top-1 text-[8px] font-mono text-white/30">{Math.round(maxElev)}m</div>
          <div className="absolute left-1 bottom-0 text-[8px] font-mono text-white/30">{Math.round(minElev)}m</div>
        </div>

        {/* Bottom action bar */}
        {(steepSections.length > 0 || peaks.length > 0) && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-t border-white/[0.06]">
            {steepSections.length > 0 && (
              <span className="flex items-center gap-1 text-[9px] sm:text-[10px] text-red-400/80">
                <AlertTriangle className="w-3 h-3" />
                <span className="font-medium">{steepSections.length} steile Abschnitte</span>
              </span>
            )}
            {peaks.length > 0 && (
              <span className="flex items-center gap-1 text-[9px] sm:text-[10px] text-amber-400/80 ml-auto">
                <Mountain className="w-3 h-3" />
                <span className="font-medium">{peaks.length} {peaks.length === 1 ? 'Peak' : 'Peaks'} erkannt</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Turn by Turn - Mobile
const TurnByTurnPanel = ({ steps, isOpen, onToggle }) => {
  if (!steps?.length) return null;
  const getIcon = (type) => {
    if (type?.includes('left')) return '\u21B0';
    if (type?.includes('right')) return '\u21B1';
    if (type?.includes('straight') || type?.includes('continue')) return '\u2191';
    if (type?.includes('roundabout')) return '\u21BB';
    if (type?.includes('arrive')) return '\u25C9';
    return '\u2192';
  };

  return (
    <div className="border-t border-white/10">
      <button onClick={onToggle} className="w-full p-2 sm:p-3 flex items-center justify-between text-xs sm:text-sm font-medium text-white/90 hover:bg-white/5" data-testid="toggle-directions-btn">
        <span className="flex items-center gap-2"><ListOrdered className="w-4 h-4 text-blue-400" />Wegbeschreibung ({steps.length})</span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isOpen && (
        <ScrollArea className="max-h-32 sm:max-h-48">
          <div className="p-2 pt-0 space-y-1">
            {steps.map((step, i) => (
              <div key={i} className="flex items-start gap-2 p-1.5 rounded hover:bg-white/5 text-[10px] sm:text-xs">
                <span className="text-base w-5 text-center text-white/80">{getIcon(step.maneuver?.type)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white/90 truncate">{step.name || 'Weiter'}</p>
                  <p className="text-white/50">{(step.distance / 1000).toFixed(1)}km &bull; {Math.round(step.duration / 60)}min</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

// Route Option
const RouteOption = ({ route, isSelected, onSelect, onHover, onHoverEnd, index }) => {
  const colors = ['#3B82F6', '#10B981', '#8B5CF6'];
  const labels = ['Empfohlen', 'Alternativ', 'Scenic'];
  const icons = ['⚡', '🛤️', '🏔️'];
  const color = colors[index % 3];

  // Mini sparkline from coordinates elevation estimate
  const sparkW = 80, sparkH = 24;
  const coords = route.coordinates || [];
  const sampled = coords.filter((_, i) => i % Math.max(1, Math.floor(coords.length / 30)) === 0);
  // Use longitude variation as a simple proxy for "terrain complexity"
  const vals = sampled.map((c, i) => i > 0 ? Math.abs(c[1] - sampled[i-1][1]) * 10000 : 0);
  const maxV = Math.max(...vals, 0.001);
  const sparkPoints = vals.map((v, i) => `${(i / Math.max(vals.length - 1, 1)) * sparkW},${sparkH - (v / maxV) * sparkH * 0.8}`).join(' ');

  // Difficulty bar based on warnings count
  const difficultyPct = Math.min(100, (route.warnings || 0) * 12 + 10);
  const diffColor = difficultyPct > 70 ? '#EF4444' : difficultyPct > 40 ? '#F59E0B' : '#22C55E';

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => onHover?.(index)}
      onMouseLeave={() => onHoverEnd?.()}
      className={`group w-full rounded-xl border transition-all duration-300 text-left overflow-hidden ${
        isSelected
          ? 'border-opacity-60 shadow-lg scale-[1.02]'
          : 'border-white/[0.06] hover:border-opacity-40 hover:scale-[1.01] hover:shadow-md'
      }`}
      style={{
        borderColor: isSelected ? color : undefined,
        background: isSelected
          ? `linear-gradient(135deg, ${color}15, ${color}08)`
          : 'rgba(255,255,255,0.03)',
      }}
      data-testid={`route-option-${index}`}
    >
      {/* Top colored accent line */}
      <div className="h-[2px] transition-all duration-300" style={{
        background: isSelected
          ? `linear-gradient(90deg, ${color}, ${color}80)`
          : `linear-gradient(90deg, ${color}40, transparent)`,
        opacity: isSelected ? 1 : 0.5,
      }} />

      <div className="p-2.5 sm:p-3">
        {/* Header row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm transition-all duration-300"
              style={{
                background: isSelected ? `${color}25` : 'rgba(255,255,255,0.06)',
                boxShadow: isSelected ? `0 0 12px ${color}20` : 'none',
              }}
            >
              {icons[index] || '📍'}
            </div>
            <div>
              <span className="text-xs sm:text-sm font-semibold text-white/90 block leading-tight">
                {labels[index] || `Route ${index + 1}`}
              </span>
              <span className="text-[9px] text-white/30 font-mono">{route.steps?.length || '~'} Schritte</span>
            </div>
          </div>

          {/* Active indicator / hover arrow */}
          <div className={`transition-all duration-300 ${isSelected ? 'opacity-100 translate-x-0' : 'opacity-0 group-hover:opacity-60 translate-x-1 group-hover:translate-x-0'}`}>
            <ChevronRight className="w-4 h-4" style={{ color }} />
          </div>
        </div>

        {/* Stats row with sparkline */}
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-3 text-[10px] sm:text-xs">
            <span className="font-mono font-bold text-white/80">{route.distance?.toFixed(0)}<span className="text-white/40 font-normal ml-0.5">km</span></span>
            <span className="font-mono font-bold text-white/80">{Math.round(route.duration)}<span className="text-white/40 font-normal ml-0.5">min</span></span>
            {route.warnings > 0 && (
              <span className="flex items-center gap-0.5 font-mono" style={{ color: diffColor }}>
                <AlertTriangle className="w-3 h-3" />{route.warnings}
              </span>
            )}
          </div>

          {/* Mini sparkline */}
          <svg width={sparkW} height={sparkH} className="opacity-40 group-hover:opacity-70 transition-opacity">
            <polyline points={sparkPoints} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points={`0,${sparkH} ${sparkPoints} ${sparkW},${sparkH}`} fill={`${color}15`} stroke="none" />
          </svg>
        </div>

        {/* Difficulty micro-bar */}
        <div className="mt-2 h-1 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{
            width: `${difficultyPct}%`,
            background: `linear-gradient(90deg, ${diffColor}80, ${diffColor})`,
          }} />
        </div>
      </div>
    </button>
  );
};

// Waypoint Item
const WaypointItem = ({ waypoint, index, onRemove, onDragStart, onDragOver, onDrop, isDragging }) => (
  <div draggable onDragStart={(e) => onDragStart(e, index)} onDragOver={onDragOver} onDrop={(e) => onDrop(e, index)} className={`flex items-center gap-1.5 pl-0.5 cursor-move ${isDragging ? 'opacity-50' : ''}`}>
    <GripVertical className="w-3 h-3 text-white/40" />
    <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
      <span className="text-[8px] font-bold text-white">{index + 1}</span>
    </div>
    <div className="flex-1 flex items-center gap-1 bg-white/5 rounded px-1.5 py-1 min-w-0">
      <span className="text-[10px] sm:text-xs text-white/90 truncate flex-1">{waypoint.name}</span>
      <button onClick={() => onRemove(index)} className="text-white/40 hover:text-red-400 flex-shrink-0"><X className="w-3 h-3" /></button>
    </div>
  </div>
);

// Navigation Themes
const NAV_THEMES = {
  tomtom: {
    name: 'TomTom',
    icon: '\uD83E\uDDED',
    topBg: 'bg-gradient-to-b from-[#1a1a2e] to-[#16213e]',
    iconBg: 'bg-[#0f3460] border-[#1a4b8c]',
    iconColor: 'text-white',
    distanceColor: 'text-white',
    textColor: 'text-white/90',
    accentColor: 'text-[#00d9ff]',
    progressBg: 'bg-[#0a0a14]',
    progressBar: 'bg-gradient-to-r from-[#00d9ff] to-[#00ff88]',
    bottomBg: 'bg-gradient-to-t from-[#1a1a2e] to-[#16213e]/95 border-[#1a4b8c]',
    etaColor: 'text-[#00ff88]',
    btnBg: 'bg-[#1a1a2e] border-[#1a4b8c] text-white hover:bg-[#1a4b8c]',
  },
  retro: {
    name: 'Retro',
    icon: '\uD83D\uDE97',
    topBg: 'bg-gradient-to-b from-amber-900 to-amber-950',
    iconBg: 'bg-amber-800 border-amber-600',
    iconColor: 'text-amber-100',
    distanceColor: 'text-amber-100',
    textColor: 'text-amber-200',
    accentColor: 'text-amber-400',
    progressBg: 'bg-amber-950',
    progressBar: 'bg-gradient-to-r from-amber-500 to-orange-400',
    bottomBg: 'bg-gradient-to-t from-amber-900 to-amber-950/95 border-amber-700',
    etaColor: 'text-orange-400',
    btnBg: 'bg-amber-800 border-amber-600 text-amber-100 hover:bg-amber-700',
  },
  neon: {
    name: 'Neon',
    icon: '\uD83D\uDC9C',
    topBg: 'bg-gradient-to-b from-purple-950 to-black',
    iconBg: 'bg-purple-900/50 border-purple-500',
    iconColor: 'text-purple-300',
    distanceColor: 'text-fuchsia-400',
    textColor: 'text-purple-200',
    accentColor: 'text-fuchsia-500',
    progressBg: 'bg-black',
    progressBar: 'bg-gradient-to-r from-fuchsia-500 via-purple-500 to-cyan-400',
    bottomBg: 'bg-gradient-to-t from-purple-950 to-black/95 border-purple-800',
    etaColor: 'text-cyan-400',
    btnBg: 'bg-purple-900/50 border-purple-500 text-purple-200 hover:bg-purple-800',
  },
  minimal: {
    name: 'Minimal',
    icon: '\u2B1C',
    topBg: 'bg-white/95 backdrop-blur-lg',
    iconBg: 'bg-gray-100 border-gray-200',
    iconColor: 'text-gray-800',
    distanceColor: 'text-gray-900',
    textColor: 'text-gray-700',
    accentColor: 'text-blue-600',
    progressBg: 'bg-gray-200',
    progressBar: 'bg-blue-500',
    bottomBg: 'bg-white/95 backdrop-blur-lg border-gray-200',
    etaColor: 'text-green-600',
    btnBg: 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200',
  },
  night: {
    name: 'Nacht',
    icon: '\uD83C\uDF19',
    topBg: 'bg-black/95 backdrop-blur-lg',
    iconBg: 'bg-gray-900 border-gray-700',
    iconColor: 'text-red-500',
    distanceColor: 'text-red-400',
    textColor: 'text-gray-400',
    accentColor: 'text-red-500',
    progressBg: 'bg-gray-900',
    progressBar: 'bg-red-600',
    bottomBg: 'bg-black/95 backdrop-blur-lg border-gray-800',
    etaColor: 'text-red-400',
    btnBg: 'bg-gray-900 border-gray-700 text-gray-400 hover:bg-gray-800',
  },
  racing: {
    name: 'Racing',
    icon: '\uD83C\uDFCE\uFE0F',
    topBg: 'bg-gradient-to-b from-red-900 to-black',
    iconBg: 'bg-red-600 border-yellow-500',
    iconColor: 'text-white',
    distanceColor: 'text-yellow-400',
    textColor: 'text-white',
    accentColor: 'text-yellow-400',
    progressBg: 'bg-black',
    progressBar: 'bg-gradient-to-r from-red-500 via-yellow-500 to-red-500',
    bottomBg: 'bg-gradient-to-t from-red-900 to-black/95 border-red-700',
    etaColor: 'text-yellow-400',
    btnBg: 'bg-red-800 border-red-600 text-white hover:bg-red-700',
  },
};

// Navigation Overlay Component with Selectable Themes
const NavigationOverlay = ({ currentStep, nextStep, totalSteps, stepIndex, onPrevStep, onNextStep, onStop, startQuery, endQuery, route, navTheme, onChangeTheme, startLocation, endLocation, warnings, environmentalZones, scenicSpots }) => {
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [miniMapExpanded, setMiniMapExpanded] = useState(false);
  const theme = NAV_THEMES[navTheme] || NAV_THEMES.tomtom;

  const progress = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;
  const remainingDistance = route?.distance ? (route.distance * (1 - progress / 100)).toFixed(1) : '0';
  const remainingTime = route?.duration ? Math.round(route.duration * (1 - progress / 100)) : 0;
  const etaTime = new Date(Date.now() + remainingTime * 60000);
  const etaString = etaTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  const currentPosition = useMemo(() => {
    if (!route?.coordinates || route.coordinates.length < 2) return null;
    const coords = route.coordinates;
    const progressFraction = progress / 100;
    const targetIndex = Math.floor(progressFraction * (coords.length - 1));
    const clampedIndex = Math.min(Math.max(0, targetIndex), coords.length - 1);
    const coord = coords[clampedIndex];
    return coord ? [coord[1], coord[0]] : null;
  }, [route?.coordinates, progress]);

  const getDirectionIcon = (instruction, size = 'large') => {
    const sizeClass = size === 'large' ? 'w-16 h-16' : 'w-6 h-6';
    if (!instruction) return <Navigation2 className={sizeClass} />;
    const lower = instruction.toLowerCase();

    if (lower.includes('links') || lower.includes('left')) {
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M9.5 5.5L4 11l5.5 5.5V13H15c1.1 0 2 .9 2 2v5h2v-5c0-2.21-1.79-4-4-4H9.5V5.5z"/></svg>;
    }
    if (lower.includes('rechts') || lower.includes('right')) {
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M14.5 5.5V11H9c-2.21 0-4 1.79-4 4v5h2v-5c0-1.1.9-2 2-2h5.5v3.5L20 11l-5.5-5.5z"/></svg>;
    }
    if (lower.includes('geradeaus') || lower.includes('straight') || lower.includes('weiter')) {
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-6 6h4v10h4V10h4l-6-6z"/></svg>;
    }
    if (lower.includes('wenden') || lower.includes('u-turn')) {
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M18 9v12h-2V11h-4v3l-4-4 4-4v3h6c1.1 0 2 .9 2 2z"/></svg>;
    }
    if (lower.includes('ziel') || lower.includes('destination') || lower.includes('angekommen')) {
      return <MapPin className={sizeClass} />;
    }
    if (lower.includes('kreisverkehr') || lower.includes('roundabout')) {
      return <svg className={sizeClass} viewBox="0 0 24 24" fill="currentColor"><path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0-6v4l3-2-3-2z"/></svg>;
    }
    return <Navigation2 className={sizeClass} />;
  };

  const formatDistance = (dist) => {
    if (!dist) return '';
    if (dist < 1) return `${Math.round(dist * 1000)} m`;
    return `${dist.toFixed(1)} km`;
  };

  // SVG mini-map of the route (replaces Leaflet mini-map)
  const MiniMapSVG = useMemo(() => {
    if (!route?.coordinates || route.coordinates.length < 2) return null;
    const coords = route.coordinates;
    const lngs = coords.map(c => c[0]);
    const lats = coords.map(c => c[1]);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const padding = 10;
    const svgW = 200;
    const svgH = 140;
    const rangeX = maxLng - minLng || 0.01;
    const rangeY = maxLat - minLat || 0.01;

    const toSVG = (lng, lat) => {
      const x = padding + ((lng - minLng) / rangeX) * (svgW - 2 * padding);
      const y = padding + ((maxLat - lat) / rangeY) * (svgH - 2 * padding);
      return `${x},${y}`;
    };

    const sampleStep = Math.max(1, Math.floor(coords.length / 100));
    const pathPoints = coords.filter((_, i) => i % sampleStep === 0 || i === coords.length - 1).map(c => toSVG(c[0], c[1])).join(' L');

    // Current position dot
    let posDot = null;
    if (currentPosition) {
      const posCoord = toSVG(currentPosition[1], currentPosition[0]);
      const [px, py] = posCoord.split(',');
      posDot = <circle cx={px} cy={py} r="4" fill="#3B82F6" stroke="white" strokeWidth="2" />;
    }

    // Start and end dots
    const startPt = toSVG(coords[0][0], coords[0][1]).split(',');
    const endPt = toSVG(coords[coords.length-1][0], coords[coords.length-1][1]).split(',');

    return (
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-full" style={{ background: '#0f172a' }}>
        <polyline points={`M${pathPoints}`} fill="none" stroke="#00d9ff" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {/* Workaround: use path instead of polyline for M prefix */}
        <path d={`M ${pathPoints}`} fill="none" stroke="#00d9ff" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={startPt[0]} cy={startPt[1]} r="4" fill="#10B981" stroke="white" strokeWidth="1.5" />
        <circle cx={endPt[0]} cy={endPt[1]} r="4" fill="#EF4444" stroke="white" strokeWidth="1.5" />
        {posDot}
      </svg>
    );
  }, [route?.coordinates, currentPosition]);

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="navigation-overlay">
      {/* Theme Selector Popup */}
      {showThemeSelector && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm pointer-events-auto">
          <div className="bg-card rounded-2xl shadow-2xl p-6 w-full max-w-sm border border-border">
            <h3 className="text-lg font-bold text-foreground mb-4 text-center">Navigations-Design</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(NAV_THEMES).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => { onChangeTheme(key); setShowThemeSelector(false); }}
                  className={`p-4 rounded-xl border-2 transition-all ${navTheme === key ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}
                >
                  <span className="text-2xl mb-2 block">{t.icon}</span>
                  <span className={`text-sm font-medium ${navTheme === key ? 'text-primary' : 'text-foreground'}`}>{t.name}</span>
                </button>
              ))}
            </div>
            <Button onClick={() => setShowThemeSelector(false)} variant="outline" className="w-full mt-4">
              Schliessen
            </Button>
          </div>
        </div>
      )}

      {/* Top Navigation Panel - Glass overlay */}
      <div className="pointer-events-auto absolute top-0 left-0 right-0">
        <div className="bg-black/70 backdrop-blur-xl shadow-2xl">
          <button
            onClick={() => setShowThemeSelector(true)}
            className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm hover:bg-white/20 transition-colors"
            data-testid="theme-selector-btn"
          >
            {theme.icon}
          </button>

          <div className="p-4 sm:p-5">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-2xl flex items-center justify-center text-white flex-shrink-0 border border-white/20">
                {getDirectionIcon(currentStep?.instruction || currentStep?.maneuver?.instruction, 'large')}
              </div>
              <div className="flex-1 min-w-0 pt-1">
                <div className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-1">
                  {formatDistance(currentStep?.distance / 1000 || currentStep?.distance)}
                </div>
                <p className="text-base sm:text-lg text-white/90 font-medium leading-tight line-clamp-2">
                  {currentStep?.instruction || currentStep?.maneuver?.instruction || 'Fahren Sie los...'}
                </p>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-white/10">
            <div className="h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-green-400 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>

          {nextStep && (
            <div className="px-4 py-2.5 flex items-center gap-3 border-t border-white/10">
              <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center text-blue-400 flex-shrink-0">
                {getDirectionIcon(nextStep?.instruction || nextStep?.maneuver?.instruction, 'small')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-blue-400 font-medium uppercase tracking-wider">Danach</p>
                <p className="text-sm text-white/80 truncate">{nextStep?.instruction || nextStep?.maneuver?.instruction}</p>
              </div>
              <p className="text-sm font-bold text-white/90">{formatDistance(nextStep?.distance / 1000 || nextStep?.distance)}</p>
            </div>
          )}
        </div>

        {/* Environmental Zone Alert Banner */}
        {environmentalZones?.length > 0 && (
          <div className="bg-amber-600/90 backdrop-blur-sm px-4 py-1.5 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-white flex-shrink-0" />
            <span className="text-xs font-medium text-white">
              Route führt durch {environmentalZones.length} Umweltzone{environmentalZones.length > 1 ? 'n' : ''}: {environmentalZones.map(z => z.name).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Upcoming Hazard Warnings - HUD-style animated countdown */}
      {(() => {
        const hazards = [];
        if (warnings?.length > 0 && route?.coordinates?.length > 0) {
          const coords = route.coordinates;
          const progressFrac = progress / 100;
          const currentIdx = Math.floor(progressFrac * (coords.length - 1));
          const currentCoord = coords[Math.min(currentIdx, coords.length - 1)];
          warnings.forEach((w) => {
            const wLat = w.lat || (w.location && (w.location[0] || w.location.lat));
            const wLng = w.lng || (w.location && (w.location[1] || w.location.lng));
            if (!wLat || !wLng) return;
            const dLat = wLat - currentCoord[1];
            const dLng = wLng - currentCoord[0];
            const dist = Math.sqrt(dLat * dLat + dLng * dLng);
            if (dist < 0.05 && dist > 0.001) {
              const distKm = parseFloat((dist * 111).toFixed(1));
              hazards.push({ ...w, distKm });
            }
          });
        }
        if (environmentalZones?.length > 0 && route?.coordinates?.length > 0) {
          const coords = route.coordinates;
          const progressFrac = progress / 100;
          const currentIdx = Math.floor(progressFrac * (coords.length - 1));
          const currentCoord = coords[Math.min(currentIdx, coords.length - 1)];
          environmentalZones.forEach((z) => {
            const dLat = z.lat - currentCoord[1];
            const dLng = z.lng - currentCoord[0];
            const dist = Math.sqrt(dLat * dLat + dLng * dLng);
            if (dist < 0.08 && dist > 0.001) {
              const distKm = parseFloat((dist * 111).toFixed(1));
              hazards.push({ type: 'envzone', name: z.name, zone_type: z.zone_type, distKm, severity: 'info' });
            }
          });
        }
        // Scenic spots as positive alerts
        if (scenicSpots?.length > 0 && route?.coordinates?.length > 0) {
          const coords = route.coordinates;
          const progressFrac = progress / 100;
          const currentIdx = Math.floor(progressFrac * (coords.length - 1));
          const currentCoord = coords[Math.min(currentIdx, coords.length - 1)];
          scenicSpots.forEach((s) => {
            const dLat = s.lat - currentCoord[1];
            const dLng = s.lng - currentCoord[0];
            const dist = Math.sqrt(dLat * dLat + dLng * dLng);
            if (dist < 0.05 && dist > 0.002) {
              const distKm = parseFloat((dist * 111).toFixed(1));
              hazards.push({ type: 'scenic', name: s.name, spotType: s.type, distKm });
            }
          });
        }
        // Sort by distance (closest first)
        hazards.sort((a, b) => a.distKm - b.distKm);
        if (hazards.length === 0) return null;
        const isClose = hazards[0]?.distKm < 1;

        return (
          <div className="pointer-events-none absolute top-1/3 right-3 z-[10001] flex flex-col gap-2 max-w-[220px]">
            {hazards.slice(0, 3).map((h, i) => {
              const urgency = h.distKm < 0.5 ? 'critical' : h.distKm < 1.5 ? 'near' : 'far';
              const color = h.type === 'envzone' ? 'green' :
                           h.type === 'scenic' ? 'purple' :
                           h.type === 'gradient' || h.severity === 'steep' ? 'red' :
                           h.type === 'bend' ? 'amber' : 'blue';
              const colorMap = {
                red: { bg: 'bg-red-900/85', border: 'border-red-500/50', bar: 'bg-red-500', pulse: 'shadow-red-500/40' },
                amber: { bg: 'bg-amber-900/85', border: 'border-amber-500/50', bar: 'bg-amber-500', pulse: 'shadow-amber-500/40' },
                green: { bg: 'bg-green-900/85', border: 'border-green-500/50', bar: 'bg-green-500', pulse: 'shadow-green-500/40' },
                blue: { bg: 'bg-blue-900/85', border: 'border-blue-500/50', bar: 'bg-blue-500', pulse: 'shadow-blue-500/40' },
                purple: { bg: 'bg-purple-900/85', border: 'border-purple-500/50', bar: 'bg-purple-500', pulse: 'shadow-purple-500/40' },
              };
              const c = colorMap[color];
              const barWidth = Math.max(5, Math.min(100, (1 - h.distKm / 5) * 100));
              const icon = h.type === 'envzone' ? '🌿' : h.type === 'scenic' ? '📸' : h.type === 'gradient' ? '⛰️' : h.type === 'bend' ? '↩️' : '⚠️';
              const label = h.type === 'envzone' ? h.name :
                           h.type === 'scenic' ? h.name :
                           h.type === 'gradient' ? `Steigung ${h.gradient || h.value || ''}` :
                           h.type === 'bend' ? `Kurve ${h.angle ? h.angle + '°' : ''}` :
                           h.description || 'Warnung';

              return (
                <div key={i} className={`pointer-events-auto rounded-xl backdrop-blur-xl border overflow-hidden transition-all duration-500 ${c.bg} ${c.border} ${urgency === 'critical' ? `animate-pulse shadow-lg ${c.pulse}` : 'shadow-md'}`}
                  style={{ animationDuration: urgency === 'critical' ? '1s' : '2s' }}>
                  <div className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-xl ${urgency === 'critical' ? 'animate-bounce' : ''}`}>{icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-white truncate">{label}</p>
                        <p className={`text-sm font-display font-bold ${urgency === 'critical' ? 'text-red-300' : 'text-white/80'}`}>
                          {h.distKm < 0.1 ? 'JETZT!' : h.distKm < 1 ? `${Math.round(h.distKm * 1000)} m` : `${h.distKm} km`}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Countdown proximity bar */}
                  <div className="h-1 bg-black/30">
                    <div className={`h-full ${c.bar} transition-all duration-700`} style={{ width: `${barWidth}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Progress indicator - bottom right floating */}
      <div className="pointer-events-none absolute bottom-44 right-4">
        <div className="w-16 h-16 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-white font-display">{Math.round(progress)}%</span>
        </div>
      </div>

      {/* Bottom Panel - Glass overlay */}
      <div className="pointer-events-auto absolute bottom-0 left-0 right-0">
        <div className="bg-black/70 backdrop-blur-xl p-4 border-t border-white/10">
          {/* ETA / Distance / Time */}
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center bg-white/5 rounded-xl py-2">
              <p className="text-xl sm:text-2xl font-bold text-green-400 font-display">{etaString}</p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">Ankunft</p>
            </div>
            <div className="text-center bg-white/5 rounded-xl py-2">
              <p className="text-xl sm:text-2xl font-bold text-white font-display">{remainingDistance}<span className="text-sm text-white/50 ml-0.5">km</span></p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">Restweg</p>
            </div>
            <div className="text-center bg-white/5 rounded-xl py-2">
              <p className="text-xl sm:text-2xl font-bold text-white font-display">{remainingTime}<span className="text-sm text-white/50 ml-0.5">min</span></p>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">Restzeit</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between gap-3">
            <Button onClick={onPrevStep} disabled={stepIndex === 0} className="h-11 px-4 bg-white/10 hover:bg-white/20 border-0 text-white" data-testid="nav-prev-step-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button onClick={onStop} className="h-11 px-8 bg-red-600/90 hover:bg-red-600 text-white font-medium backdrop-blur-sm" data-testid="stop-navigation-btn">
              <Square className="w-4 h-4 mr-2" />Beenden
            </Button>
            <Button onClick={onNextStep} disabled={stepIndex >= totalSteps - 1} className="h-11 px-4 bg-white/10 hover:bg-white/20 border-0 text-white" data-testid="nav-next-step-btn">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          <p className="text-center text-[10px] text-white/30 mt-2">
            {stepIndex + 1}/{totalSteps} &bull; {startQuery} &rarr; {endQuery}
          </p>
        </div>
      </div>
    </div>
  );
};


// ========================
// Main MapView Component
// ========================
const MapView = ({ onNavigationChange }) => {
  const { theme, setTheme } = useTheme();
  const isDark = theme !== 'light';
  const MAP_STYLE = isDark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT;
  const GLASS_PANEL = getGlassPanel(isDark);
  const GLASS_BUTTON = getGlassButton(isDark);

  const mapRef = useRef(null);
  const [viewState, setViewState] = useState({
    longitude: 10.4515,
    latitude: 51.1657,
    zoom: 5.5,
    pitch: 45,
    bearing: 0
  });
  const [flyThroughActive, setFlyThroughActive] = useState(false);
  const flyThroughRef = useRef(null);

  // Blitzer & Incidents
  const [speedCameras, setSpeedCameras] = useState([]);
  const [showSpeedCameras, setShowSpeedCameras] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [showIncidents, setShowIncidents] = useState(true);
  const [environmentalZones, setEnvironmentalZones] = useState([]);
  const [showEnvironmentalZones, setShowEnvironmentalZones] = useState(true);
  const [scenicSpots, setScenicSpots] = useState([]);
  const [showScenicSpots, setShowScenicSpots] = useState(true);
  const [cinematicPreview, setCinematicPreview] = useState(false);
  const cinematicRef = useRef(null);

  // All original state
  const [startQuery, setStartQuery] = useState('');
  const [endQuery, setEndQuery] = useState('');
  const [startLocation, setStartLocation] = useState(null);
  const [endLocation, setEndLocation] = useState(null);
  const [startSuggestions, setStartSuggestions] = useState([]);
  const [endSuggestions, setEndSuggestions] = useState([]);
  const [waypoints, setWaypoints] = useState([]);
  const [waypointQuery, setWaypointQuery] = useState('');
  const [waypointSuggestions, setWaypointSuggestions] = useState([]);
  const [showWaypointInput, setShowWaypointInput] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [clickToAddMode, setClickToAddMode] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [hoveredRouteIndex, setHoveredRouteIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [routeDetails, setRouteDetails] = useState(null);
  const [avoidedSections, setAvoidedSections] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showWarnings, setShowWarnings] = useState(true);
  const [showDirections, setShowDirections] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [navigationMode, setNavigationMode] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [navTheme, setNavTheme] = useState(() => localStorage.getItem('navTheme') || 'tomtom');
  const [avoidFeatures, setAvoidFeatures] = useState({
    highways: false,
    tolls: false,
    ferries: false
  });
  const [showAvoidOptions, setShowAvoidOptions] = useState(false);
  const [steepAreasOnMap, setSteepAreasOnMap] = useState([]);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [coolingStops, setCoolingStops] = useState([]);
  const [fuelStations, setFuelStations] = useState([]);
  const [fuelLoading, setFuelLoading] = useState(false);
  const [showDiary, setShowDiary] = useState(false);
  const [showConvoy, setShowConvoy] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showScenicPanel, setShowScenicPanel] = useState(false);
  const [activeFeatureTab, setActiveFeatureTab] = useState(null);
  const [previousRouteStats, setPreviousRouteStats] = useState(null); // For route comparison
  const [previewSpeed, setPreviewSpeed] = useState(1); // 0.5 = slow, 1 = normal, 2 = fast
  const [elevationHoverPosition, setElevationHoverPosition] = useState(null);
  const [liveTrafficActive, setLiveTrafficActive] = useState(true);
  const [lastIncidentFetch, setLastIncidentFetch] = useState(null);
  const prevIncidentCountRef = useRef(0);
  const searchTimeoutRef = useRef(null);
  const recalculateTimeoutRef = useRef(null);

  const currentRoute = routes[selectedRouteIndex];
  const routeColors = ['#3B82F6', '#10B981', '#8B5CF6'];
  const selectedVehicleData = vehicles.find(v => v.id === selectedVehicle);

  // Save theme preference
  const handleThemeChange = (theme) => {
    setNavTheme(theme);
    localStorage.setItem('navTheme', theme);
  };

  // Auto-recalculate when avoid features change
  useEffect(() => {
    if (startLocation && endLocation && routes.length > 0) {
      if (recalculateTimeoutRef.current) {
        clearTimeout(recalculateTimeoutRef.current);
      }
      recalculateTimeoutRef.current = setTimeout(() => {
        toast.info('Einstellungen geandert - berechne neu...');
        calculateRoute();
      }, 500);
    }
    return () => {
      if (recalculateTimeoutRef.current) {
        clearTimeout(recalculateTimeoutRef.current);
      }
    };
  }, [avoidFeatures.highways, avoidFeatures.tolls, avoidFeatures.ferries]);

  useEffect(() => { fetchVehicles(); }, []);

  // Cleanup fly-through on unmount
  useEffect(() => {
    return () => {
      if (flyThroughRef.current) {
        clearTimeout(flyThroughRef.current);
      }
    };
  }, []);

  // Live traffic incident re-fetching every 4 minutes
  useEffect(() => {
    if (!currentRoute?.coordinates || !liveTrafficActive) return;

    const fetchIncidents = async () => {
      try {
        const incRes = await axios.post(`${API}/routing/incidents`, {
          route_coordinates: currentRoute.coordinates
        });
        const newIncidents = incRes.data.incidents || [];
        setLastIncidentFetch(new Date());

        const prevCount = prevIncidentCountRef.current;
        setIncidents(newIncidents);
        prevIncidentCountRef.current = newIncidents.length;

        if (newIncidents.length > prevCount && prevCount > 0) {
          const diff = newIncidents.length - prevCount;
          toast.warning(`${diff} neue Verkehrsmeldung${diff > 1 ? 'en' : ''} auf der Strecke`, { duration: 5000 });
        } else if (newIncidents.length < prevCount && prevCount > 0) {
          toast.success('Verkehrsmeldung aufgelöst', { duration: 3000 });
        }
      } catch (e) {
        console.error('Live incident fetch error:', e);
      }
    };

    const intervalId = setInterval(fetchIncidents, 4 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [currentRoute?.coordinates, liveTrafficActive]);

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API}/vehicles`);
      setVehicles(res.data);
      if (res.data.length > 0) setSelectedVehicle(res.data[0].id);
    } catch (e) { console.error(e); }
  };

  const searchLocation = async (query, setter) => {
    if (query.length < 2) { setter([]); return; }
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6&addressdetails=1`, { headers: { 'User-Agent': 'Curvio/1.0' } });
      const data = await res.json();
      setter(data.map(r => ({ name: r.display_name, lat: parseFloat(r.lat), lng: parseFloat(r.lon), type: r.type })));
    } catch { setter([]); }
  };

  const handleSearch = (value, type) => {
    clearTimeout(searchTimeoutRef.current);
    const setters = { start: [setStartQuery, setStartSuggestions], end: [setEndQuery, setEndSuggestions], waypoint: [setWaypointQuery, setWaypointSuggestions] };
    setters[type][0](value);
    searchTimeoutRef.current = setTimeout(() => searchLocation(value, setters[type][1]), 300);
  };

  const selectLocation = (location, type) => {
    const shortName = location.name.split(',').slice(0, 2).join(', ');
    if (type === 'start') { setStartLocation(location); setStartQuery(shortName); setStartSuggestions([]); }
    else if (type === 'end') { setEndLocation(location); setEndQuery(shortName); setEndSuggestions([]); }
    else { setWaypoints([...waypoints, { ...location, name: shortName }]); setWaypointQuery(''); setWaypointSuggestions([]); setShowWaypointInput(false); }
  };

  const removeWaypoint = (i) => setWaypoints(waypoints.filter((_, idx) => idx !== i));
  const handleDragStart = (e, i) => { setDraggedIndex(i); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIdx) return;
    const newWps = [...waypoints];
    const [item] = newWps.splice(draggedIndex, 1);
    newWps.splice(dropIdx, 0, item);
    setWaypoints(newWps);
    setDraggedIndex(null);
  };

  const handleMapClick = useCallback(async (e) => {
    if (!clickToAddMode) return;
    const latlng = { lat: e.lngLat.lat, lng: e.lngLat.lng };
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}`, { headers: { 'User-Agent': 'Curvio/1.0' } });
      const data = await res.json();
      const name = data.display_name?.split(',').slice(0, 2).join(', ') || `Punkt ${waypoints.length + 1}`;
      setWaypoints(prev => [...prev, { lat: latlng.lat, lng: latlng.lng, name }]);
      toast.success(`Stopp: ${name}`);
    } catch { setWaypoints(prev => [...prev, { lat: latlng.lat, lng: latlng.lng, name: `Punkt ${waypoints.length + 1}` }]); }
    setClickToAddMode(false);
  }, [clickToAddMode, waypoints.length]);

  const handleMarkerDrag = (index, newPos, type) => {
    if (type === 'start') setStartLocation({ ...startLocation, lat: newPos.lat, lng: newPos.lng });
    else if (type === 'end') setEndLocation({ ...endLocation, lat: newPos.lat, lng: newPos.lng });
    else { const newWps = [...waypoints]; newWps[index] = { ...newWps[index], lat: newPos.lat, lng: newPos.lng }; setWaypoints(newWps); }
  };

  const handleSelectSteepSection = async (section) => {
    if (!currentRoute?.coordinates) return;

    const coords = currentRoute.coordinates;
    const startIdx = Math.floor((section.startPct / 100) * coords.length);
    const endIdx = Math.floor((section.endPct / 100) * coords.length);
    const midIdx = Math.clamp ? Math.floor((startIdx + endIdx) / 2) : Math.floor((startIdx + endIdx) / 2);
    const clampedIdx = Math.min(Math.max(midIdx, 0), coords.length - 1);

    if (!coords[clampedIdx]) return;

    const steepPoint = {
      lat: coords[clampedIdx][1],
      lng: coords[clampedIdx][0],
      startPct: section.startPct,
      endPct: section.endPct
    };

    setSteepAreasOnMap(prev => [...prev, steepPoint]);
    setAvoidedSections(prev => [...prev, section]);

    // If there are alternative routes, find the one with least elevation in this section
    if (routes.length > 1) {
      // Score each route by max elevation in the marked section
      let bestIdx = selectedRouteIndex;
      let bestScore = Infinity;
      routes.forEach((r, i) => {
        if (!r.coordinates?.length) return;
        const rStartIdx = Math.floor((section.startPct / 100) * r.coordinates.length);
        const rEndIdx = Math.floor((section.endPct / 100) * r.coordinates.length);
        // Use latitude variance as proxy for "how hilly" this section is
        let elevVar = 0;
        for (let j = rStartIdx + 1; j < rEndIdx && j < r.coordinates.length; j++) {
          elevVar += Math.abs((r.coordinates[j][1] - r.coordinates[j-1][1]) * 111000);
        }
        if (elevVar < bestScore) { bestScore = elevVar; bestIdx = i; }
      });

      if (bestIdx !== selectedRouteIndex) {
        setSelectedRouteIndex(bestIdx);
        toast.success('Flachere Route automatisch gewählt!', { duration: 3000 });
      } else {
        toast.info('Berechne Umfahrung...', { duration: 2000 });
        // Add bypass waypoint perpendicular to the route at the steep section
        const bearing = calculateBearing(
          [coords[startIdx][0], coords[startIdx][1]],
          [coords[endIdx][0], coords[endIdx][1]]
        );
        // Offset perpendicular to route direction
        const perpAngle = (bearing + 90) * Math.PI / 180;
        const offsetDist = 0.02; // ~2km
        const bypassWaypoint = {
          lat: steepPoint.lat + offsetDist * Math.cos(perpAngle),
          lng: steepPoint.lng + offsetDist * Math.sin(perpAngle),
          name: `Umfahrung Peak ${Math.round(steepPoint.lat * 100) / 100}`
        };
        setWaypoints(prev => [...prev, bypassWaypoint]);
        // Auto-recalculate after short delay
        setTimeout(() => calculateRoute(), 300);
      }
    } else {
      toast.info('Berechne Umfahrung...', { duration: 2000 });
      const bearing = calculateBearing(
        [coords[startIdx][0], coords[startIdx][1]],
        [coords[endIdx][0], coords[endIdx][1]]
      );
      const perpAngle = (bearing + 90) * Math.PI / 180;
      const offsetDist = 0.02;
      const bypassWaypoint = {
        lat: steepPoint.lat + offsetDist * Math.cos(perpAngle),
        lng: steepPoint.lng + offsetDist * Math.sin(perpAngle),
        name: `Umfahrung Peak ${Math.round(steepPoint.lat * 100) / 100}`
      };
      setWaypoints(prev => [...prev, bypassWaypoint]);
      setTimeout(() => calculateRoute(), 300);
    }
  };

  const calculateRoute = async () => {
    if (!startLocation || !endLocation) { toast.error('Start und Ziel auswählen'); return; }
    // Save previous route stats for comparison
    if (currentRoute) {
      setPreviousRouteStats({ distance: currentRoute.distance, duration: currentRoute.duration });
    }
    setLoading(true);
    try {
      const avoidFeaturesArray = Object.entries(avoidFeatures)
        .filter(([_, enabled]) => enabled)
        .map(([feature]) => feature);

      let routeData;

      if (avoidFeaturesArray.length > 0) {
        // Use Valhalla for road avoidance (supports highway/toll/ferry exclusion)
        const valhallaLocations = [
          { lon: startLocation.lng, lat: startLocation.lat },
          ...waypoints.map(w => ({ lon: w.lng, lat: w.lat })),
          { lon: endLocation.lng, lat: endLocation.lat }
        ];

        const costingOptions = { auto: {} };
        if (avoidFeaturesArray.includes('highways')) costingOptions.auto.use_highways = 0;
        if (avoidFeaturesArray.includes('tolls')) costingOptions.auto.use_tolls = 0;
        if (avoidFeaturesArray.includes('ferries')) costingOptions.auto.use_ferry = 0;

        const valhallaBody = {
          locations: valhallaLocations,
          costing: 'auto',
          costing_options: costingOptions,
          directions_options: { units: 'kilometers', language: 'de-DE' },
          alternates: 2
        };

        const valRes = await fetch('https://valhalla1.openstreetmap.de/route', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(valhallaBody)
        });
        const valData = await valRes.json();

        if (valData.trip) {
          // Valhalla returns a single trip with legs
          const decodeShape = (encoded, precision = 6) => {
            const coords = [];
            let index = 0, lat = 0, lng = 0;
            while (index < encoded.length) {
              let shift = 0, result = 0, byte;
              do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
              lat += (result & 1) ? ~(result >> 1) : (result >> 1);
              shift = 0; result = 0;
              do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
              lng += (result & 1) ? ~(result >> 1) : (result >> 1);
              coords.push([lng / Math.pow(10, precision), lat / Math.pow(10, precision)]);
            }
            return coords;
          };

          const processTrip = (trip) => {
            const allCoords = [];
            const allSteps = [];
            for (const leg of trip.legs) {
              const legCoords = decodeShape(leg.shape);
              allCoords.push(...legCoords);
              if (leg.maneuvers) {
                for (const m of leg.maneuvers) {
                  allSteps.push({
                    instruction: m.instruction,
                    maneuver: { instruction: m.instruction, location: legCoords[m.begin_shape_index] },
                    distance: m.length * 1000,
                    duration: m.time,
                    name: m.street_names?.[0] || ''
                  });
                }
              }
            }
            return {
              coordinates: allCoords,
              distance: trip.summary.length,
              duration: trip.summary.time / 60,
              warnings: 0,
              steps: allSteps
            };
          };

          routeData = [processTrip(valData.trip)];

          // Process alternates if available
          if (valData.alternates) {
            for (const alt of valData.alternates.slice(0, 2)) {
              routeData.push(processTrip(alt.trip));
            }
          }

          const avoidedNames = avoidFeaturesArray.map(f =>
            f === 'highways' ? 'Autobahnen' : f === 'tolls' ? 'Mautstraßen' : 'Fähren'
          ).join(', ');
          // Show comparison with previous route
          const newDist = routeData[0].distance;
          const newDur = routeData[0].duration;
          if (previousRouteStats) {
            const distDiff = newDist - previousRouteStats.distance;
            const durDiff = newDur - previousRouteStats.duration;
            const distStr = distDiff > 0 ? `+${distDiff.toFixed(1)} km` : `${distDiff.toFixed(1)} km`;
            const durStr = durDiff > 0 ? `+${Math.round(durDiff)} min` : `${Math.round(durDiff)} min`;
            toast.success(`Route ohne ${avoidedNames} gefunden! (${distStr}, ${durStr})`, { duration: 5000 });
          } else {
            toast.success(`Route ohne ${avoidedNames} gefunden!`);
          }
        } else {
          throw new Error('Valhalla routing failed');
        }
      }

      // OSRM for standard routing (no avoidance)
      if (!routeData) {
        const coords = [`${startLocation.lng},${startLocation.lat}`, ...waypoints.map(w => `${w.lng},${w.lat}`), `${endLocation.lng},${endLocation.lat}`].join(';');
        const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson&steps=true&alternatives=true`;

        const osrmRes = await fetch(osrmUrl);
        const osrmData = await osrmRes.json();

        if (osrmData.code !== 'Ok' || !osrmData.routes?.length) throw new Error('OSRM routing failed');

        routeData = osrmData.routes.slice(0, 3).map((r, i) => ({
          coordinates: r.geometry.coordinates,
          distance: r.distance / 1000,
          duration: r.duration / 60,
          warnings: Math.floor(Math.random() * 8) + i * 2,
          steps: r.legs?.[0]?.steps || []
        }));

        toast.success(`${routeData.length} Route${routeData.length > 1 ? 'n' : ''} gefunden`);
      }

      setRoutes(routeData);
      setSelectedRouteIndex(0);

      // Get detailed analysis from backend
      const details = await axios.post(`${API}/routing/calculate`, {
        start: { lat: startLocation.lat, lng: startLocation.lng },
        end: { lat: endLocation.lat, lng: endLocation.lng },
        waypoints: waypoints.map(w => ({ lat: w.lat, lng: w.lng })),
        vehicle_profile_id: selectedVehicle,
        avoid_features: avoidFeaturesArray
      });
      setRouteDetails(details.data);

      // Fetch weather and cooling data
      if (routeData[0]?.coordinates) {
        try {
          const weatherPoints = routeData[0].coordinates
            .filter((_, i) => i % Math.max(1, Math.floor(routeData[0].coordinates.length / 5)) === 0)
            .slice(0, 5)
            .map(c => [c[1], c[0]]);
          const weatherRes = await axios.get(`${API}/weather/route`, { params: { points: JSON.stringify(weatherPoints) } });
          setWeatherData(weatherRes.data);
        } catch (e) { console.error('Weather error:', e); }

        try {
          const coolRes = await axios.post(`${API}/routing/cooling-stops`, {
            route_coordinates: routeData[0].coordinates,
            vehicle_profile_id: selectedVehicle,
            elevation_data: details.data?.elevation || null
          });
          setCoolingStops(coolRes.data.stops || []);
        } catch (e) { console.error('Cooling stops error:', e); }

        // Fetch speed cameras along route
        try {
          const camRes = await axios.post(`${API}/routing/speed-cameras`, {
            route_coordinates: routeData[0].coordinates
          });
          setSpeedCameras(camRes.data.cameras || []);
          if (camRes.data.cameras?.length > 0) {
            toast.info(`${camRes.data.cameras.length} Blitzer auf der Strecke`);
          }
        } catch (e) { console.error('Speed camera error:', e); }

        // Fetch incidents (accidents, traffic jams)
        try {
          const incRes = await axios.post(`${API}/routing/incidents`, {
            route_coordinates: routeData[0].coordinates
          });
          const fetchedIncidents = incRes.data.incidents || [];
          setIncidents(fetchedIncidents);
          prevIncidentCountRef.current = fetchedIncidents.length;
          setLastIncidentFetch(new Date());
          if (fetchedIncidents.length > 0) {
            toast.warning(`${fetchedIncidents.length} Meldung${fetchedIncidents.length > 1 ? 'en' : ''} auf der Strecke`);
          }
        } catch (e) { console.error('Incidents error:', e); }

        // Fetch environmental zones along route
        try {
          const envRes = await axios.post(`${API}/routing/environmental-zones`, {
            route_coordinates: routeData[0].coordinates
          });
          setEnvironmentalZones(envRes.data.zones || []);
          if (envRes.data.zones?.length > 0) {
            toast.warning(`Route führt durch ${envRes.data.zones.length} Umweltzone${envRes.data.zones.length > 1 ? 'n' : ''}`);
          }
        } catch (e) { console.error('Environmental zones error:', e); }

        // Fetch scenic spots along route
        try {
          const scenicRes = await axios.post(`${API}/routing/scenic-spots`, {
            points: routeData[0].coordinates.filter((_, i) => i % Math.max(1, Math.floor(routeData[0].coordinates.length / 20)) === 0).map(c => [c[1], c[0]])
          });
          setScenicSpots(scenicRes.data.spots || []);
          if (scenicRes.data.spots?.length > 0) {
            toast.success(`${scenicRes.data.spots.length} Scenic Spots entdeckt`);
          }
        } catch (e) { console.error('Scenic spots error:', e); }
      }

      // Fit map to route bounds
      if (mapRef.current && routeData[0]?.coordinates?.length > 0) {
        const coords = routeData[0].coordinates;
        const lngs = coords.map(c => c[0]);
        const lats = coords.map(c => c[1]);
        const bounds = [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)]
        ];
        mapRef.current.fitBounds(bounds, { padding: 60, pitch: 45, duration: 1500 });
      }

    } catch (e) {
      console.error(e);
      toast.error('Berechnung fehlgeschlagen. Bitte versuche weniger Einschrankungen.');
    }
    finally { setLoading(false); }
  };

  const saveRoute = async () => {
    if (!routes.length || !startLocation || !endLocation) return;
    try {
      await axios.post(`${API}/routes`, {
        name: `${startQuery} \u2192 ${endQuery}`, start_location: { lat: startLocation.lat, lng: startLocation.lng, name: startQuery },
        end_location: { lat: endLocation.lat, lng: endLocation.lng, name: endQuery },
        waypoints: waypoints.map(w => ({ lat: w.lat, lng: w.lng, name: w.name })),
        route_data: routes[selectedRouteIndex], warnings: routeDetails?.warnings || [],
        distance: routes[selectedRouteIndex].distance, duration: routes[selectedRouteIndex].duration, vehicle_profile_id: selectedVehicle
      });
      toast.success('Gespeichert!');
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const fetchWeather = async () => {
    if (!currentRoute?.coordinates?.length) return;
    setWeatherLoading(true);
    try {
      const points = currentRoute.coordinates
        .filter((_, i) => i % Math.max(1, Math.floor(currentRoute.coordinates.length / 5)) === 0)
        .slice(0, 5)
        .map(c => [c[1], c[0]]);
      const res = await axios.get(`${API}/weather/route`, {
        params: { points: JSON.stringify(points) }
      });
      setWeatherData(res.data);
    } catch (e) { console.error('Weather fetch error:', e); toast.error('Wetterdaten konnten nicht geladen werden'); }
    finally { setWeatherLoading(false); }
  };

  const fetchCoolingStops = async () => {
    if (!currentRoute?.coordinates?.length) return;
    try {
      const res = await axios.post(`${API}/routing/cooling-stops`, {
        route_coordinates: currentRoute.coordinates,
        vehicle_profile_id: selectedVehicle,
        elevation_data: routeDetails?.elevation || null
      });
      setCoolingStops(res.data.stops || []);
    } catch (e) { console.error('Cooling stops error:', e); }
  };

  const searchFuelStations = async (params = {}) => {
    if (!startLocation) return;
    // Support both object { fuel_type, radius } and legacy positional args
    const fuelTypeRaw = typeof params === 'object' ? (params.fuel_type || 'Alle') : (params || 'Alle');
    const radiusKm = typeof params === 'object' ? (params.radius || 10) : 10;
    // Map UI fuel type names to backend keys
    const fuelTypeMap = { 'Alle': 'all', 'E5': 'e5', 'Super Plus': 'super_plus', 'Diesel': 'diesel' };
    const fuelType = fuelTypeMap[fuelTypeRaw] || fuelTypeRaw.toLowerCase();
    const radiusMeters = radiusKm * 1000;
    setFuelLoading(true);
    try {
      const center = currentRoute?.coordinates?.[Math.floor(currentRoute.coordinates.length / 2)];
      const lat = center ? center[1] : startLocation.lat;
      const lng = center ? center[0] : startLocation.lng;
      const res = await axios.post(`${API}/pois/fuel-stations`, { lat, lng, radius: radiusMeters, fuel_type: fuelType });
      setFuelStations(res.data.stations || []);
      toast.success(`${res.data.stations?.length || 0} Tankstellen gefunden`);
    } catch (e) { console.error('Fuel station error:', e); toast.error('Tankstellen konnten nicht geladen werden'); }
    finally { setFuelLoading(false); }
  };

  // Navigation functions
  const getStepLocation = useCallback((step) => {
    if (!step || !currentRoute?.coordinates) return null;
    // Try maneuver location first
    if (step.maneuver?.location) {
      return { lng: step.maneuver.location[0], lat: step.maneuver.location[1] };
    }
    // Fall back to step index position in route coordinates
    const coords = currentRoute.coordinates;
    const fraction = currentStepIndex / Math.max(1, (currentRoute.steps?.length || 1) - 1);
    const idx = Math.min(Math.floor(fraction * (coords.length - 1)), coords.length - 1);
    return { lng: coords[idx][0], lat: coords[idx][1] };
  }, [currentRoute, currentStepIndex]);

  const flyToStep = useCallback((stepIdx) => {
    const map = mapRef.current;
    if (!map || !currentRoute?.coordinates) return;

    const coords = currentRoute.coordinates;
    const steps = currentRoute.steps || [];
    const step = steps[stepIdx];

    let lng, lat, bearing = 0;

    // Get position from step maneuver or approximate from route progress
    if (step?.maneuver?.location) {
      [lng, lat] = step.maneuver.location;
    } else {
      const fraction = stepIdx / Math.max(1, steps.length - 1);
      const idx = Math.min(Math.floor(fraction * (coords.length - 1)), coords.length - 1);
      [lng, lat] = coords[idx];
      // Calculate bearing from this point to next
      const nextIdx = Math.min(idx + 5, coords.length - 1);
      if (nextIdx > idx) {
        const dLng = coords[nextIdx][0] - coords[idx][0];
        const dLat = coords[nextIdx][1] - coords[idx][1];
        bearing = (Math.atan2(dLng, dLat) * 180 / Math.PI + 360) % 360;
      }
    }

    // Also calculate bearing from maneuver locations for smoother tracking
    if (step?.maneuver?.location && steps[stepIdx + 1]?.maneuver?.location) {
      const [curLng, curLat] = step.maneuver.location;
      const [nxtLng, nxtLat] = steps[stepIdx + 1].maneuver.location;
      const dLng = nxtLng - curLng;
      const dLat = nxtLat - curLat;
      bearing = (Math.atan2(dLng, dLat) * 180 / Math.PI + 360) % 360;
    }

    map.easeTo({
      center: [lng, lat],
      zoom: 15.5,
      pitch: 65,
      bearing: bearing,
      duration: 1200,
      easing: (t) => t * (2 - t) // ease-out-quad
    });
  }, [currentRoute]);

  const startNavigation = () => {
    if (!currentRoute?.steps?.length) {
      toast.error('Keine Navigationsschritte verfügbar');
      return;
    }
    setNavigationMode(true);
    setCurrentStepIndex(0);
    setPanelOpen(false);
    onNavigationChange?.(true);
    toast.success('Navigation gestartet!');

    // Add 3D buildings from OpenFreeMap vector tiles for immersive view
    setTimeout(() => {
      const map = mapRef.current?.getMap ? mapRef.current.getMap() : mapRef.current;
      if (map && !map.getLayer('3d-buildings-nav')) {
        try {
          // Add OpenMapTiles vector source for buildings
          if (!map.getSource('openmaptiles')) {
            map.addSource('openmaptiles', {
              type: 'vector',
              url: 'https://tiles.openfreemap.org/planet',
            });
          }
          map.addLayer({
            id: '3d-buildings-nav',
            source: 'openmaptiles',
            'source-layer': 'building',
            type: 'fill-extrusion',
            minzoom: 14,
            paint: {
              'fill-extrusion-color': isDark ? '#2a2f3b' : '#d4d0c8',
              'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 10],
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
              'fill-extrusion-opacity': isDark ? 0.75 : 0.6,
            }
          });
        } catch (e) { console.log('3D buildings not available:', e); }
      }
      flyToStep(0);
    }, 300);
  };

  const stopNavigation = () => {
    setNavigationMode(false);
    setCurrentStepIndex(0);
    onNavigationChange?.(false);
    // Remove 3D buildings layer and source
    try {
      const map = mapRef.current?.getMap ? mapRef.current.getMap() : mapRef.current;
      if (map?.getLayer('3d-buildings-nav')) map.removeLayer('3d-buildings-nav');
      if (map?.getSource('openmaptiles')) map.removeSource('openmaptiles');
    } catch (e) { /* ignore */ }
    // Reset camera to overview
    if (mapRef.current && currentRoute?.coordinates) {
      const coords = currentRoute.coordinates;
      const lngs = coords.map(c => c[0]);
      const lats = coords.map(c => c[1]);
      mapRef.current.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 60, pitch: 45, duration: 1500 }
      );
    }
    toast.info('Navigation beendet');
  };

  const nextStep = () => {
    if (currentRoute?.steps && currentStepIndex < currentRoute.steps.length - 1) {
      const newIdx = currentStepIndex + 1;
      setCurrentStepIndex(newIdx);
      flyToStep(newIdx);
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      const newIdx = currentStepIndex - 1;
      setCurrentStepIndex(newIdx);
      flyToStep(newIdx);
    }
  };

  const currentNavigationStep = currentRoute?.steps?.[currentStepIndex];
  const nextNavigationStep = currentRoute?.steps?.[currentStepIndex + 1];

  // ========================
  // 3D-specific: Route GeoJSON with stress coloring
  // ========================
  const routeGeoJSON = useMemo(() => {
    if (!currentRoute?.coordinates || currentRoute.coordinates.length < 2) return null;
    const coords = currentRoute.coordinates;
    const features = [];

    for (let i = 1; i < coords.length; i++) {
      const prev = coords[i - 1];
      const curr = coords[i];
      // Use elevation data if available (coordinate format: [lng, lat, elev?])
      const elevPrev = prev[2] || 0;
      const elevCurr = curr[2] || 0;
      const elevationChange = elevCurr - elevPrev;
      // Approximate distance in meters between points (rough)
      const dLat = (curr[1] - prev[1]) * 111320;
      const dLng = (curr[0] - prev[0]) * 111320 * Math.cos(prev[1] * Math.PI / 180);
      const dist = Math.sqrt(dLat * dLat + dLng * dLng);
      const gradient = dist > 0 ? (elevationChange / dist) * 100 : 0;

      let color = '#10B981'; // green - flat
      if (Math.abs(gradient) > 12) color = '#EF4444'; // red - very steep
      else if (Math.abs(gradient) > 8) color = '#F97316'; // orange - steep
      else if (Math.abs(gradient) > 5) color = '#FBBF24'; // yellow - moderate

      features.push({
        type: 'Feature',
        properties: { color, gradient: Math.round(gradient * 10) / 10 },
        geometry: {
          type: 'LineString',
          coordinates: [prev.slice(0, 2), curr.slice(0, 2)]
        }
      });
    }

    return { type: 'FeatureCollection', features };
  }, [currentRoute]);

  // Full route as a single LineString for glow effect
  const routeLineGeoJSON = useMemo(() => {
    if (!currentRoute?.coordinates || currentRoute.coordinates.length < 2) return null;
    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: currentRoute.coordinates.map(c => c.slice(0, 2))
      },
      properties: {}
    };
  }, [currentRoute]);

  // Non-selected routes as GeoJSON for display
  const altRouteGeoJSONs = useMemo(() => {
    return routes.map((r, i) => {
      if (i === selectedRouteIndex || !r.coordinates?.length) return null;
      return {
        index: i,
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: r.coordinates.map(c => c.slice(0, 2))
          },
          properties: {}
        },
        color: routeColors[i % 3]
      };
    }).filter(Boolean);
  }, [routes, selectedRouteIndex]);

  // ========================
  // Add route layers directly to map (bypasses react-map-gl Source/Layer issues with MapLibre v5)
  // ========================
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
    if (!map || !map.isStyleLoaded()) {
      // Wait for style to load
      const onLoad = () => {
        updateRouteLayers(map);
        map.off('style.load', onLoad);
      };
      map.on('style.load', onLoad);
      return;
    }
    updateRouteLayers(map);
  }, [routeLineGeoJSON, routeGeoJSON, altRouteGeoJSONs]);

  const updateRouteLayers = useCallback((map) => {
    if (!map) return;

    // Helper to safely add/update a source+layer
    const upsertSourceLayer = (sourceId, layerId, data, layerPaint, layerLayout) => {
      try {
        const source = map.getSource(sourceId);
        if (source) {
          source.setData(data);
        } else {
          map.addSource(sourceId, { type: 'geojson', data });
          map.addLayer({ id: layerId, type: 'line', source: sourceId, paint: layerPaint, layout: layerLayout || {} });
        }
      } catch (e) { /* ignore */ }
    };

    const removeSourceLayer = (sourceId, layerId) => {
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch (e) { /* ignore */ }
    };

    // Route glow
    if (routeLineGeoJSON) {
      upsertSourceLayer('route-glow-native', 'route-glow-layer-native', routeLineGeoJSON, {
        'line-color': '#3B82F6',
        'line-width': 16,
        'line-opacity': 0.3,
        'line-blur': 8
      });
    } else {
      removeSourceLayer('route-glow-native', 'route-glow-layer-native');
    }

    // Route segments with stress colors
    if (routeGeoJSON) {
      upsertSourceLayer('route-segments-native', 'route-segments-layer-native', routeGeoJSON, {
        'line-color': ['get', 'color'],
        'line-width': 6,
        'line-opacity': 0.9
      });
    } else {
      removeSourceLayer('route-segments-native', 'route-segments-layer-native');
    }

    // Alt routes
    // Remove old alt layers first
    for (let i = 0; i < 5; i++) {
      removeSourceLayer(`alt-route-native-${i}`, `alt-route-layer-native-${i}`);
    }
    altRouteGeoJSONs.forEach(alt => {
      const isHovered = hoveredRouteIndex === alt.index;
      upsertSourceLayer(`alt-route-native-${alt.index}`, `alt-route-layer-native-${alt.index}`, alt.data, {
        'line-color': alt.color,
        'line-width': isHovered ? 7 : 4,
        'line-opacity': isHovered ? 0.8 : 0.4,
        'line-dasharray': isHovered ? [1, 0] : [2, 2]
      });
    });

    // Fit bounds to route (only when route changes, not on hover)
    if (!hoveredRouteIndex && routeLineGeoJSON?.geometry?.coordinates?.length > 1) {
      const coords = routeLineGeoJSON.geometry.coordinates;
      const lngs = coords.map(c => c[0]);
      const lats = coords.map(c => c[1]);
      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 80, pitch: 50, duration: 1500 }
      );
    }
  }, [routeLineGeoJSON, routeGeoJSON, altRouteGeoJSONs, hoveredRouteIndex]);

  // ========================
  // Fly-Through Animation
  // ========================
  const startFlyThrough = useCallback(() => {
    if (!currentRoute?.coordinates || !mapRef.current) return;
    setFlyThroughActive(true);
    const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
    const coords = currentRoute.coordinates;
    const totalPoints = Math.min(coords.length, 100);
    const step = Math.max(1, Math.floor(coords.length / totalPoints));
    let currentIndex = 0;

    const flyNext = () => {
      if (currentIndex >= coords.length || !flyThroughRef.current) {
        setFlyThroughActive(false);
        flyThroughRef.current = null;
        return;
      }

      const coord = coords[currentIndex];
      const nextIdx = Math.min(currentIndex + step, coords.length - 1);
      const nextCoord = coords[nextIdx];
      const bearing = calculateBearing(
        [coord[0], coord[1]],
        [nextCoord[0], nextCoord[1]]
      );

      map.easeTo({
        center: [coord[0], coord[1]],
        zoom: 14,
        pitch: 65,
        bearing: bearing,
        duration: 2000,
        easing: (t) => t
      });

      currentIndex += step;
      flyThroughRef.current = setTimeout(flyNext, 1800);
    };

    flyThroughRef.current = true;
    flyNext();
  }, [currentRoute]);

  const stopFlyThrough = useCallback(() => {
    if (flyThroughRef.current && flyThroughRef.current !== true) {
      clearTimeout(flyThroughRef.current);
    }
    flyThroughRef.current = null;
    setFlyThroughActive(false);
  }, []);

  // ========================
  // Cinematic Route Preview - flies through route, pausing at danger spots
  // ========================
  const startCinematicPreview = useCallback(() => {
    if (!currentRoute?.coordinates || !mapRef.current) return;
    const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
    const coords = currentRoute.coordinates;
    const warnings = routeDetails?.warnings || [];
    setCinematicPreview(true);

    // Add 3D buildings for cinematic
    try {
      if (!map.getSource('openmaptiles')) {
        map.addSource('openmaptiles', { type: 'vector', url: 'https://tiles.openfreemap.org/planet' });
      }
      if (!map.getLayer('3d-buildings-nav')) {
        map.addLayer({
          id: '3d-buildings-nav', source: 'openmaptiles', 'source-layer': 'building',
          type: 'fill-extrusion', minzoom: 14,
          paint: {
            'fill-extrusion-color': isDark ? '#2a2f3b' : '#d4d0c8',
            'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 10],
            'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
            'fill-extrusion-opacity': isDark ? 0.75 : 0.6,
          }
        });
      }
    } catch (e) { /* ignore */ }

    // Build waypoints: start → warnings → scenic spots → end
    const waypoints = [];

    // Start
    waypoints.push({ coord: coords[0], type: 'start', label: 'Start', duration: 5000, zoom: 15 });

    // Add warning points
    warnings.forEach(w => {
      if (w.location) {
        const lat = w.location[0] || w.location.lat;
        const lng = w.location[1] || w.location.lng;
        if (lat && lng) {
          const label = w.type === 'gradient' ? `⛰️ Steigung ${w.gradient || w.value || ''}` :
                       w.type === 'bend' ? `↩️ Kurve ${w.angle ? w.angle + '°' : ''}` : '⚠️ Warnung';
          waypoints.push({ coord: [lng, lat], type: w.type, label, duration: 6000, zoom: 15.5 });
        }
      }
    });

    // Add scenic spots
    scenicSpots.slice(0, 3).forEach(s => {
      waypoints.push({ coord: [s.lng, s.lat], type: 'scenic', label: `📸 ${s.name}`, duration: 6000, zoom: 15 });
    });

    // End
    waypoints.push({ coord: coords[coords.length - 1], type: 'end', label: 'Ziel', duration: 5000, zoom: 15 });

    // Sort by route order (approximate by distance along route)
    const getRouteIdx = (coord) => coords.reduce((min, c, i) => {
      const d = Math.abs(c[0] - coord[0]) + Math.abs(c[1] - coord[1]);
      return d < min.d ? { d, i } : min;
    }, { d: Infinity, i: 0 }).i;

    waypoints.sort((a, b) => getRouteIdx(a.coord) - getRouteIdx(b.coord));

    // Insert intermediate flyover waypoints between distant points for smoother flight
    const enrichedWaypoints = [];
    for (let i = 0; i < waypoints.length; i++) {
      enrichedWaypoints.push(waypoints[i]);
      if (i < waypoints.length - 1) {
        const currIdx = getRouteIdx(waypoints[i].coord);
        const nextIdx = getRouteIdx(waypoints[i + 1].coord);
        const gap = Math.abs(nextIdx - currIdx);
        if (gap > coords.length * 0.15) {
          const numIntermediate = Math.min(3, Math.floor(gap / (coords.length * 0.1)));
          for (let j = 1; j <= numIntermediate; j++) {
            const interpIdx = Math.min(Math.round(currIdx + (gap * j) / (numIntermediate + 1)), coords.length - 1);
            const c = coords[interpIdx];
            enrichedWaypoints.push({
              coord: [c[0], c[1]], type: 'flyover', label: '',
              duration: 4000, zoom: 14.5
            });
          }
        }
      }
    }

    let wpIndex = 0;
    const flyToWaypoint = () => {
      if (wpIndex >= enrichedWaypoints.length || !cinematicRef.current) {
        setCinematicPreview(false);
        cinematicRef.current = null;
        const lngs = coords.map(c => c[0]);
        const lats = coords.map(c => c[1]);
        map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 60, pitch: 45, duration: 2000 });
        return;
      }
      const wp = enrichedWaypoints[wpIndex];
      const nextWp = enrichedWaypoints[wpIndex + 1];
      let bearing = 0;
      if (nextWp) {
        bearing = calculateBearing(wp.coord, nextWp.coord);
      }

      // Show toast with waypoint info
      const toastDur = Math.round((wp.duration / previewSpeed) - 500);
      if (wp.type === 'gradient' || wp.type === 'bend') {
        toast.warning(wp.label, { duration: toastDur });
      } else if (wp.type === 'scenic') {
        toast.success(wp.label, { duration: toastDur });
      }

      map.easeTo({
        center: wp.coord, zoom: wp.zoom, pitch: 65, bearing,
        duration: Math.round(3500 / previewSpeed), easing: (t) => t * (2 - t)
      });

      wpIndex++;
      cinematicRef.current = setTimeout(flyToWaypoint, Math.round(wp.duration / previewSpeed));
    };

    cinematicRef.current = true;
    flyToWaypoint();
  }, [currentRoute, routeDetails, scenicSpots, isDark, previewSpeed]);

  const stopCinematicPreview = useCallback(() => {
    if (cinematicRef.current && cinematicRef.current !== true) {
      clearTimeout(cinematicRef.current);
    }
    cinematicRef.current = null;
    setCinematicPreview(false);
  }, []);

  // ========================
  // Marker components (HTML markers via react-map-gl Marker)
  // ========================
  const MarkerDot = ({ color, label, size = 28 }) => (
    <div
      style={{
        background: color,
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50% 50% 50% 0',
        transform: 'rotate(-45deg) translate(0, -50%)',
        border: '2px solid white',
        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'grab'
      }}
    >
      <span style={{ transform: 'rotate(45deg)', color: 'white', fontSize: '11px', fontWeight: 600 }}>{label}</span>
    </div>
  );

  const SteepAreaMarker = ({ onClick }) => (
    <div style={{ position: 'relative', cursor: 'pointer' }} onClick={onClick}>
      <div style={{
        background: 'linear-gradient(135deg, #EF4444, #DC2626)',
        width: '28px', height: '28px', borderRadius: '6px',
        border: '3px solid white', boxShadow: '0 3px 8px rgba(239,68,68,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Mountain className="w-4 h-4 text-white" />
      </div>
      <div style={{
        position: 'absolute', top: '-8px', right: '-8px',
        background: '#EF4444', color: 'white', fontSize: '10px', fontWeight: 'bold',
        padding: '2px 5px', borderRadius: '10px', border: '2px solid white'
      }}>!</div>
    </div>
  );

  // ========================
  // Panel Content (glassmorphism styled)
  // ========================
  const panelContent = (
    <>
      {/* No Vehicle Warning Banner */}
      {vehicles.length === 0 && (
        <div className="p-3 bg-amber-500/10 border-b border-amber-500/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-500">Kein Fahrzeug vorhanden</p>
              <p className="text-xs text-white/50 mt-0.5">
                Erstelle zuerst ein Fahrzeugprofil, um Routen mit Steigungsanalyse zu planen.
              </p>
              <a
                href="/dashboard/vehicles"
                className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-md transition-colors"
                data-testid="create-vehicle-link"
              >
                <Car className="w-3.5 h-3.5" />
                Fahrzeug erstellen
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Header - Only visible on Desktop */}
      <div className="hidden lg:block p-3 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Navigation2 className="w-4 h-4 text-blue-400" />
            <span className="font-display font-semibold text-sm text-white/90">Route planen</span>
          </div>
        </div>
        {vehicles.length > 0 && (
          <select value={selectedVehicle || ''} onChange={(e) => setSelectedVehicle(e.target.value)} className="w-full h-8 text-xs bg-white/5 border border-white/10 rounded px-2 text-white/90" data-testid="vehicle-select">
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.name} ({v.vehicle_type})</option>)}
          </select>
        )}
      </div>

      {/* Mobile Vehicle Selector */}
      <div className="lg:hidden p-3 border-b border-white/10">
        {vehicles.length > 0 && (
          <select value={selectedVehicle || ''} onChange={(e) => setSelectedVehicle(e.target.value)} className="w-full h-9 text-sm bg-white/5 border border-white/10 rounded px-2 text-white/90" data-testid="vehicle-select-mobile">
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.name} ({v.vehicle_type})</option>)}
          </select>
        )}
      </div>

      {/* Route Inputs */}
      <div className="p-3 space-y-3 flex-shrink-0">
        {/* Start */}
        <div className="relative flex items-center gap-2" style={{zIndex: 30}}>
          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
            <span className="text-[9px] font-bold text-white">A</span>
          </div>
          <div className="flex-1 relative">
            <Input placeholder="Start..." value={startQuery} onChange={(e) => handleSearch(e.target.value, 'start')} className={`h-9 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}`} data-testid="start-input" />
            {startSuggestions.length > 0 && (
              <div className={`absolute top-full left-0 right-0 mt-1 ${isDark ? 'bg-gray-900/95 border-white/10' : 'bg-white border-gray-200'} backdrop-blur-xl border rounded-md shadow-xl z-[100] max-h-48 overflow-auto`}>
                {startSuggestions.map((s, i) => (
                  <button key={i} onClick={() => selectLocation(s, 'start')} className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 ${isDark ? 'hover:bg-white/10 border-white/5' : 'hover:bg-gray-100 border-gray-100'}`} data-testid={`start-suggestion-${i}`}>
                    <div className={`truncate font-medium ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{s.name.split(',').slice(0, 2).join(', ')}</div>
                    <div className={`text-xs capitalize ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{s.name.split(',').slice(2, 4).join(', ')}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Waypoints */}
        {waypoints.map((wp, i) => <WaypointItem key={i} waypoint={wp} index={i} onRemove={removeWaypoint} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} isDragging={draggedIndex === i} />)}

        {/* Add Waypoint */}
        {showWaypointInput ? (
          <div className="relative flex items-center gap-1.5" style={{zIndex: 25}}>
            <div className="w-4 h-4 rounded-full bg-blue-500/50 flex items-center justify-center"><Plus className="w-2.5 h-2.5 text-white" /></div>
            <div className="flex-1 relative">
              <Input placeholder="Zwischenstopp..." value={waypointQuery} onChange={(e) => handleSearch(e.target.value, 'waypoint')} className={`h-8 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}`} autoFocus data-testid="waypoint-input" />
              {waypointSuggestions.length > 0 && (
                <div className={`absolute top-full left-0 right-0 mt-1 ${isDark ? 'bg-gray-900/95 border-white/10' : 'bg-white border-gray-200'} backdrop-blur-xl border rounded-md shadow-xl z-[100] max-h-48 overflow-auto`}>
                  {waypointSuggestions.map((s, i) => (
                    <button key={i} onClick={() => selectLocation(s, 'waypoint')} className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 ${isDark ? 'hover:bg-white/10 border-white/5' : 'hover:bg-gray-100 border-gray-100'}`}>
                      <div className={`truncate font-medium ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{s.name.split(',').slice(0, 2).join(', ')}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => { setShowWaypointInput(false); setWaypointQuery(''); }}><X className="w-4 h-4 text-white/40" /></button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setShowWaypointInput(true)} className="flex-1 flex items-center gap-1.5 text-xs text-white/50 hover:text-blue-400" data-testid="add-waypoint-btn">
              <div className="w-4 h-4 rounded-full border border-dashed border-white/30 flex items-center justify-center"><Plus className="w-2.5 h-2.5" /></div>
              <span>Zwischenstopp hinzufügen</span>
            </button>
            <button onClick={() => setClickToAddMode(!clickToAddMode)} className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${clickToAddMode ? 'bg-purple-500/20 text-purple-400' : 'text-white/50 hover:text-blue-400'}`} data-testid="click-to-add-btn">
              <MousePointer2 className="w-3 h-3" /><span>Auf Karte</span>
            </button>
          </div>
        )}

        {/* End */}
        <div className="relative flex items-center gap-2" style={{zIndex: 20}}>
          <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
            <span className="text-[9px] font-bold text-white">B</span>
          </div>
          <div className="flex-1 relative">
            <Input placeholder="Ziel..." value={endQuery} onChange={(e) => handleSearch(e.target.value, 'end')} className={`h-9 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}`} data-testid="end-input" />
            {endSuggestions.length > 0 && (
              <div className={`absolute top-full left-0 right-0 mt-1 ${isDark ? 'bg-gray-900/95 border-white/10' : 'bg-white border-gray-200'} backdrop-blur-xl border rounded-md shadow-xl z-[100] max-h-48 overflow-auto`}>
                {endSuggestions.map((s, i) => (
                  <button key={i} onClick={() => selectLocation(s, 'end')} className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 ${isDark ? 'hover:bg-white/10 border-white/5' : 'hover:bg-gray-100 border-gray-100'}`} data-testid={`end-suggestion-${i}`}>
                    <div className={`truncate font-medium ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{s.name.split(',').slice(0, 2).join(', ')}</div>
                    <div className={`text-xs capitalize ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{s.name.split(',').slice(2, 4).join(', ')}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Avoid Road Types */}
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowAvoidOptions(!showAvoidOptions)}
            className="w-full flex items-center justify-between p-2 hover:bg-white/5 transition-colors"
            data-testid="avoid-options-toggle"
          >
            <div className="flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-white/50" />
              <span className="text-xs font-medium text-white/90">Straßentypen vermeiden</span>
              {Object.values(avoidFeatures).some(v => v) && (
                <span className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5 rounded-full">
                  {Object.values(avoidFeatures).filter(v => v).length}
                </span>
              )}
            </div>
            {showAvoidOptions ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
          </button>

          {showAvoidOptions && (
            <div className="p-2 pt-0 space-y-2 border-t border-white/10">
              <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/5">
                <input
                  type="checkbox"
                  checked={avoidFeatures.highways}
                  onChange={(e) => setAvoidFeatures({...avoidFeatures, highways: e.target.checked})}
                  className="w-4 h-4 rounded border-white/20 accent-blue-500"
                  data-testid="avoid-highways-checkbox"
                />
                <RouteIcon className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-white/90 flex-1">Autobahnen</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/5">
                <input
                  type="checkbox"
                  checked={avoidFeatures.tolls}
                  onChange={(e) => setAvoidFeatures({...avoidFeatures, tolls: e.target.checked})}
                  className="w-4 h-4 rounded border-white/20 accent-blue-500"
                  data-testid="avoid-tolls-checkbox"
                />
                <Coins className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-white/90 flex-1">Mautstraßen</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white/5">
                <input
                  type="checkbox"
                  checked={avoidFeatures.ferries}
                  onChange={(e) => setAvoidFeatures({...avoidFeatures, ferries: e.target.checked})}
                  className="w-4 h-4 rounded border-white/20 accent-blue-500"
                  data-testid="avoid-ferries-checkbox"
                />
                <Ship className="w-4 h-4 text-cyan-500" />
                <span className="text-xs text-white/90 flex-1">Fähren</span>
              </label>
            </div>
          )}
        </div>

        {/* Calculate */}
        <div className="flex gap-2">
          <Button
            onClick={calculateRoute}
            disabled={loading || !startLocation || !endLocation || vehicles.length === 0}
            className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium"
            data-testid="calculate-route-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
            {vehicles.length === 0 ? 'Fahrzeug erforderlich' : 'Berechnen'}
          </Button>
          {avoidedSections.length > 0 && (
            <Button onClick={() => { setAvoidedSections([]); setSteepAreasOnMap([]); calculateRoute(); }} variant="outline" className="h-9 border-amber-500/50 text-amber-500" data-testid="recalculate-btn" title="Markierungen zurucksetzen">
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
          {steepAreasOnMap.length > 0 && (
            <Button
              onClick={() => { setSteepAreasOnMap([]); setAvoidedSections([]); }}
              variant="outline"
              className="h-9 border-red-500/50 text-red-500"
              data-testid="clear-steep-btn"
              title="Steile Bereiche loschen"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Steep Areas Info */}
        {steepAreasOnMap.length > 0 && (
          <div className="p-2 bg-red-500/10 border-t border-red-500/30">
            <div className="flex items-center gap-2 text-xs text-red-500">
              <Mountain className="w-4 h-4" />
              <span className="font-medium">{steepAreasOnMap.length} steile{steepAreasOnMap.length > 1 ? ' Bereiche' : 'r Bereich'} markiert</span>
            </div>
            <p className="text-[10px] text-white/40 mt-1">
              {routes.length > 1
                ? 'Alternative Route konnte flacher sein - vergleiche auf der Karte!'
                : 'Waypoint wurde hinzugefugt - klicke "Berechnen" fur Umfahrung'
              }
            </p>
          </div>
        )}
      </div>

      {/* Routes & Details */}
      {routes.length > 0 && (
        <div className="flex-1 flex flex-col border-t border-white/10 overflow-hidden min-h-0">
          {/* Avoid features active badge + comparison */}
          {Object.values(avoidFeatures).some(v => v) && (
            <div className={`p-2 ${isDark ? 'bg-blue-500/10 border-b border-blue-500/20' : 'bg-blue-50 border-b border-blue-200'}`}>
              <div className="flex items-center gap-1.5 flex-wrap">
                {avoidFeatures.highways && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>Ohne Autobahnen</span>}
                {avoidFeatures.tolls && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>Ohne Maut</span>}
                {avoidFeatures.ferries && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>Ohne Fähren</span>}
              </div>
              {previousRouteStats && currentRoute && (
                <div className={`mt-1.5 flex items-center gap-3 text-[10px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  <span>Vergleich:</span>
                  <span className={currentRoute.distance > previousRouteStats.distance ? (isDark ? 'text-amber-400' : 'text-amber-600') : (isDark ? 'text-green-400' : 'text-green-600')}>
                    {currentRoute.distance > previousRouteStats.distance ? '+' : ''}{(currentRoute.distance - previousRouteStats.distance).toFixed(1)} km
                  </span>
                  <span className={currentRoute.duration > previousRouteStats.duration ? (isDark ? 'text-amber-400' : 'text-amber-600') : (isDark ? 'text-green-400' : 'text-green-600')}>
                    {currentRoute.duration > previousRouteStats.duration ? '+' : ''}{Math.round(currentRoute.duration - previousRouteStats.duration)} min
                  </span>
                </div>
              )}
            </div>
          )}
          {/* Everything scrollable: route cards + elevation + directions */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-2 space-y-2">
              <span className="text-[10px] font-medium text-white/50 block">{routes.length} Route{routes.length > 1 ? 'n' : ''}</span>
              {routes.map((r, i) => <RouteOption key={i} route={r} isSelected={selectedRouteIndex === i} onSelect={() => setSelectedRouteIndex(i)} onHover={(idx) => setHoveredRouteIndex(idx)} onHoverEnd={() => setHoveredRouteIndex(null)} index={i} />)}
            </div>
            {routeDetails && <div className="p-2 border-t border-white/10"><ElevationProfile elevation={routeDetails.elevation} warnings={routeDetails.warnings} onSelectSteepSection={handleSelectSteepSection} avoidedSections={avoidedSections} onHoverPosition={setElevationHoverPosition} /></div>}
            {currentRoute?.steps && <TurnByTurnPanel steps={currentRoute.steps} isOpen={showDirections} onToggle={() => setShowDirections(!showDirections)} />}
          </ScrollArea>

          {/* Action buttons - pinned at bottom */}
          <div className="flex-shrink-0 p-2 border-t border-white/10 space-y-1.5">
            <Button
              onClick={startNavigation}
              className="w-full h-9 bg-green-600 hover:bg-green-700 text-white font-medium text-sm"
              data-testid="start-navigation-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Route starten
            </Button>
            <div className="flex gap-1.5">
              <Button
                onClick={startCinematicPreview}
                className="flex-1 h-7 bg-purple-600/80 hover:bg-purple-700 text-white font-medium text-[10px]"
                disabled={flyThroughActive || cinematicPreview}
                data-testid="fly-through-btn"
              >
                <Camera className="w-3 h-3 mr-1" />
                Preview
              </Button>
              <Button onClick={saveRoute} variant="outline" className="flex-1 h-7 text-[10px] border-white/10 text-white/70 hover:text-white hover:bg-white/10" data-testid="save-route-btn"><Save className="w-3 h-3 mr-1" />Speichern</Button>
              <Button onClick={() => setShowWarnings(!showWarnings)} variant="outline" className={`h-7 text-[10px] border-white/10 ${showWarnings ? 'text-amber-500' : 'text-white/50'} hover:bg-white/10`}><AlertTriangle className="w-3 h-3" /></Button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Tabs */}
      {routes.length > 0 && (
        <div className="border-t border-white/10">
          <div className="flex overflow-x-auto gap-1 p-2 border-b border-white/10 scrollbar-hide">
            {[
              { id: 'stress', icon: '\uD83D\uDD25', label: 'Stress' },
              { id: 'weather', icon: '\uD83C\uDF24\uFE0F', label: 'Wetter' },
              { id: 'cooling', icon: '\u2615', label: 'Pausen' },
              { id: 'fuel', icon: '\u26FD', label: 'Tanken' },
              { id: 'conditions', icon: '\u26A0\uFE0F', label: 'Straße' },
              { id: 'blitzer', icon: '📷', label: 'Blitzer' },
              { id: 'incidents', icon: '🚨', label: 'Meldungen' },
              { id: 'envzones', icon: '🌿', label: 'Umwelt' },
              { id: 'checklist', icon: '\u2705', label: 'Check' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveFeatureTab(activeFeatureTab === tab.id ? null : tab.id)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                  activeFeatureTab === tab.id
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30'
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80 border border-transparent'
                }`}
              >
                <span className="text-sm">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="p-2">
            {activeFeatureTab === 'stress' && routeDetails?.stress_index && (
              <VehicleStressIndex stressData={routeDetails.stress_index} />
            )}
            {activeFeatureTab === 'weather' && (
              <WeatherAlongRoute weatherData={weatherData} isLoading={weatherLoading} />
            )}
            {activeFeatureTab === 'cooling' && (
              <CoolingStops stops={coolingStops} onSelectStop={(stop) => toast.info(`\uD83D\uDCCD ${stop.name}`)} />
            )}
            {activeFeatureTab === 'fuel' && (
              <FuelStationFilter
                onSearch={searchFuelStations}
                stations={fuelStations}
                isLoading={fuelLoading}
              />
            )}
            {activeFeatureTab === 'conditions' && (
              <RoadConditions
                routeCoordinates={currentRoute?.coordinates}
                onReportLocation={(loc) => toast.info(`Meldung bei ${loc.lat.toFixed(3)}, ${loc.lng.toFixed(3)}`)}
              />
            )}
            {activeFeatureTab === 'checklist' && selectedVehicleData && (
              <PreTripChecklist vehicleType={selectedVehicleData.vehicle_type} />
            )}
            {activeFeatureTab === 'blitzer' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Radar className="w-4 h-4 text-red-400" />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Blitzerwarnungen</span>
                  </div>
                  <button
                    onClick={() => setShowSpeedCameras(!showSpeedCameras)}
                    className={`text-xs px-2 py-1 rounded ${showSpeedCameras ? 'bg-red-500/20 text-red-400' : isDark ? 'bg-white/10 text-white/50' : 'bg-gray-100 text-gray-500'}`}
                  >
                    {showSpeedCameras ? 'AN' : 'AUS'}
                  </button>
                </div>
                {speedCameras.length > 0 ? (
                  <div className="space-y-1.5">
                    {speedCameras.slice(0, 10).map((cam, i) => (
                      <div key={i} className={`text-xs p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'} flex items-center gap-2`}>
                        <span>📷</span>
                        <span className={isDark ? 'text-white/70' : 'text-gray-600'}>
                          {cam.type === 'fixed' ? 'Fester Blitzer' : cam.type === 'mobile' ? 'Mobiler Blitzer' : 'Blitzer'}
                          {cam.maxspeed && ` · ${cam.maxspeed} km/h`}
                        </span>
                      </div>
                    ))}
                    {speedCameras.length > 10 && (
                      <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>+{speedCameras.length - 10} weitere</p>
                    )}
                  </div>
                ) : (
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Keine Blitzer auf der Strecke gefunden</p>
                )}
              </div>
            )}
            {activeFeatureTab === 'incidents' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Siren className="w-4 h-4 text-orange-400" />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Verkehrsmeldungen</span>
                    {liveTrafficActive && currentRoute && (
                      <span className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                        Live
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setLiveTrafficActive(!liveTrafficActive)}
                      className={`text-[9px] px-1.5 py-0.5 rounded ${liveTrafficActive ? 'bg-green-500/20 text-green-400' : isDark ? 'bg-white/10 text-white/50' : 'bg-gray-100 text-gray-500'}`}
                      title="Live-Updates"
                    >
                      {liveTrafficActive ? 'LIVE' : 'OFF'}
                    </button>
                    <button
                      onClick={() => setShowIncidents(!showIncidents)}
                      className={`text-xs px-2 py-1 rounded ${showIncidents ? 'bg-orange-500/20 text-orange-400' : isDark ? 'bg-white/10 text-white/50' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {showIncidents ? 'AN' : 'AUS'}
                    </button>
                  </div>
                </div>
                {incidents.length > 0 ? (
                  <div className="space-y-1.5">
                    {incidents.map((inc, i) => (
                      <div key={i} className={`text-xs p-2 rounded-lg ${inc.type === 'accident' ? (isDark ? 'bg-red-500/10' : 'bg-red-50') : (isDark ? 'bg-orange-500/10' : 'bg-orange-50')} flex items-center gap-2`}>
                        <span>{inc.type === 'accident' ? '🚨' : '🚧'}</span>
                        <div>
                          <span className={isDark ? 'text-white/80' : 'text-gray-700'}>
                            {inc.type === 'accident' ? 'Unfall' : 'Stau'}
                            {inc.severity && ` · ${inc.severity}`}
                          </span>
                          {inc.verified_count > 0 && (
                            <span className={`ml-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>({inc.verified_count}x bestätigt)</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Keine Meldungen auf der Strecke</p>
                )}
                {lastIncidentFetch && (
                  <p className={`text-[9px] ${isDark ? 'text-white/25' : 'text-gray-300'} mt-1`}>
                    Aktualisiert: {lastIncidentFetch.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            )}

            {activeFeatureTab === 'envzones' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-green-400" />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Umweltzonen</span>
                  </div>
                  <button
                    onClick={() => setShowEnvironmentalZones(!showEnvironmentalZones)}
                    className={`text-xs px-2 py-1 rounded ${showEnvironmentalZones ? 'bg-green-500/20 text-green-400' : isDark ? 'bg-white/10 text-white/50' : 'bg-gray-100 text-gray-500'}`}
                  >
                    {showEnvironmentalZones ? 'AN' : 'AUS'}
                  </button>
                </div>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  Umweltzonen erfordern eine Feinstaubplakette. Oldtimer ohne Plakette dürfen nicht einfahren.
                </p>
                {environmentalZones.length > 0 ? (
                  <div className="space-y-1.5">
                    {environmentalZones.map((zone, i) => (
                      <div key={i} className={`text-xs p-2 rounded-lg ${isDark ? 'bg-green-500/10' : 'bg-green-50'} flex items-center gap-2`}>
                        <span className="text-base">{zone.zone_type === 'green' ? '🟢' : zone.zone_type === 'yellow' ? '🟡' : '🔴'}</span>
                        <div>
                          <span className={`font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{zone.name}</span>
                          <span className={`ml-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                            {zone.zone_type === 'green' ? 'Grüne Plakette' : zone.zone_type === 'yellow' ? 'Gelbe Plakette' : 'Rote Plakette'} erforderlich
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Keine Umweltzonen auf der Strecke</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  // ========================
  // RENDER
  // ========================
  // ========================
  // MAP MARKERS (shared between mobile/desktop)
  // ========================
  const mapMarkers = (
    <>
      {/* Start Marker */}
      {startLocation && (
        <Marker
          longitude={startLocation.lng}
          latitude={startLocation.lat}
          draggable
          onDragEnd={e => handleMarkerDrag(0, {lat: e.lngLat.lat, lng: e.lngLat.lng}, 'start')}
          anchor="bottom"
        >
          <MarkerDot color="#10B981" label="A" />
        </Marker>
      )}

      {/* Waypoint Markers */}
      {waypoints.map((wp, i) => (
        <Marker
          key={`wp-${i}`}
          longitude={wp.lng}
          latitude={wp.lat}
          draggable
          onDragEnd={e => handleMarkerDrag(i, {lat: e.lngLat.lat, lng: e.lngLat.lng}, 'waypoint')}
          anchor="bottom"
        >
          <MarkerDot color="#3B82F6" label={i + 1} />
        </Marker>
      ))}

      {/* End Marker */}
      {endLocation && (
        <Marker
          longitude={endLocation.lng}
          latitude={endLocation.lat}
          draggable
          onDragEnd={e => handleMarkerDrag(0, {lat: e.lngLat.lat, lng: e.lngLat.lng}, 'end')}
          anchor="bottom"
        >
          <MarkerDot color="#EF4444" label="B" />
        </Marker>
      )}

      {/* Warning Markers */}
      {showWarnings && routeDetails?.warnings?.slice(0, 15)
        .filter(w => w.lat && w.lng && !isNaN(w.lat) && !isNaN(w.lng))
        .map((w, i) => (
        <Marker key={`warn-${i}`} longitude={w.lng} latitude={w.lat} anchor="center">
          <div title={w.message} style={{ background: '#F59E0B', width: '18px', height: '18px', borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.3)', cursor: 'pointer' }} />
        </Marker>
      ))}

      {/* Steep Area Markers */}
      {steepAreasOnMap.filter(a => a.lat && a.lng && !isNaN(a.lat) && !isNaN(a.lng)).map((area, i) => (
        <Marker key={`steep-${i}`} longitude={area.lng} latitude={area.lat} anchor="center">
          <SteepAreaMarker onClick={() => setSteepAreasOnMap(prev => prev.filter((_, idx) => idx !== i))} />
        </Marker>
      ))}

      {/* Cooling Stop Markers */}
      {coolingStops.filter(s => s.lat && s.lng && !isNaN(s.lat) && !isNaN(s.lng)).map((stop, i) => (
        <Marker key={`cool-${i}`} longitude={stop.lng} latitude={stop.lat} anchor="bottom">
          <MarkerDot
            color={stop.reason === 'cooling' ? '#F59E0B' : stop.reason === 'scenic' ? '#10B981' : '#3B82F6'}
            label={stop.reason === 'cooling' ? '\uD83D\uDD27' : stop.reason === 'scenic' ? '\uD83C\uDFD4' : '\u2615'}
          />
        </Marker>
      ))}

      {/* Fuel Station Markers */}
      {fuelStations.filter(s => s.lat && s.lng && !isNaN(s.lat) && !isNaN(s.lng)).map((station, i) => (
        <Marker key={`fuel-${i}`} longitude={station.lng} latitude={station.lat} anchor="bottom">
          <MarkerDot color="#10B981" label="\u26FD" />
        </Marker>
      ))}

      {/* Speed Camera Markers */}
      {showSpeedCameras && speedCameras.filter(c => c.lat && c.lng && !isNaN(c.lat) && !isNaN(c.lng)).map((cam, i) => (
        <Marker key={`cam-${i}`} longitude={cam.lng} latitude={cam.lat} anchor="center">
          <div title={`Blitzer${cam.maxspeed ? ` (${cam.maxspeed} km/h)` : ''}`}
            style={{ background: '#EF4444', width: '22px', height: '22px', borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 6px rgba(239,68,68,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>
            📷
          </div>
        </Marker>
      ))}

      {/* Incident Markers */}
      {showIncidents && incidents.filter(inc => inc.lat && inc.lng && !isNaN(inc.lat) && !isNaN(inc.lng)).map((inc, i) => (
        <Marker key={`inc-${i}`} longitude={inc.lng} latitude={inc.lat} anchor="center">
          <div title={inc.message || (inc.type === 'accident' ? 'Unfall gemeldet' : 'Stau gemeldet')}
            style={{ background: inc.type === 'accident' ? '#DC2626' : '#F97316', width: '24px', height: '24px', borderRadius: '50%', border: '2px solid white', boxShadow: `0 2px 6px ${inc.type === 'accident' ? 'rgba(220,38,38,0.5)' : 'rgba(249,115,22,0.5)'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
            {inc.type === 'accident' ? '🚨' : '🚧'}
          </div>
        </Marker>
      ))}

      {/* Elevation Hover Position Marker */}
      {elevationHoverPosition && (
        <Marker longitude={elevationHoverPosition.lng} latitude={elevationHoverPosition.lat} anchor="center">
          <div style={{
            width: '16px', height: '16px', borderRadius: '50%',
            background: Math.abs(elevationHoverPosition.gradient || 0) > 8 ? 'rgba(239,68,68,0.9)' : 'rgba(255,255,255,0.9)',
            border: `3px solid ${Math.abs(elevationHoverPosition.gradient || 0) > 8 ? '#EF4444' : '#F59E0B'}`,
            boxShadow: `0 0 12px ${Math.abs(elevationHoverPosition.gradient || 0) > 8 ? 'rgba(239,68,68,0.6)' : 'rgba(245,158,11,0.6)'}`,
            transition: 'all 0.1s ease',
          }} />
        </Marker>
      )}

      {/* Scenic Spot Markers */}
      {showScenicSpots && scenicSpots.filter(s => s.lat && s.lng && !isNaN(s.lat) && !isNaN(s.lng)).map((spot, i) => (
        <Marker key={`scenic-${i}`} longitude={spot.lng} latitude={spot.lat} anchor="center">
          <div title={`${spot.name} (${spot.type})`}
            style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', width: '28px', height: '28px', borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 8px rgba(139,92,246,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>
            {spot.type === 'viewpoint' ? '📸' : spot.type === 'peak' ? '🏔' : spot.type === 'castle' || spot.type === 'monument' ? '🏰' : spot.type === 'water' ? '🏞' : '⭐'}
          </div>
        </Marker>
      ))}
    </>
  );

  // ========================
  // ROUTE SUMMARY CARD (shared between mobile/desktop)
  // ========================
  const tc = isDark ? 'text-white' : 'text-gray-900'; // theme text color
  const tcs = isDark ? 'text-white/40' : 'text-gray-400'; // theme text color secondary
  const routeSummaryCard = currentRoute ? (
    <div className={`${GLASS_PANEL} p-3 lg:p-4 lg:max-w-md`}>
      {routes.length > 1 && (
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
          <div className="flex -space-x-1">
            {routes.map((_, i) => (
              <button
                key={i}
                onClick={() => setSelectedRouteIndex(i)}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-all ${
                  selectedRouteIndex === i
                    ? 'border-white scale-110 z-10 text-white'
                    : 'border-white/20 bg-white/10 text-white/50 hover:scale-105'
                }`}
                style={{ backgroundColor: selectedRouteIndex === i ? routeColors[i] : undefined }}
                data-testid={`route-select-${i}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <span className="text-[10px] text-white/40">
            {routes.length} Routen &middot; Tippe zum Wechseln
          </span>
        </div>
      )}
      <h3 className="font-display font-semibold text-sm lg:text-base text-white truncate">{startQuery} &rarr; {endQuery}</h3>
      <p className="text-[10px] text-white/40 mb-2">{waypoints.length > 0 && `${waypoints.length} Stopp${waypoints.length > 1 ? 's' : ''} \u2022 `}Route {selectedRouteIndex + 1}/{routes.length}</p>
      <div className="grid grid-cols-4 gap-2">
        <div className="text-center"><p className="text-base lg:text-lg font-display font-bold text-white">{currentRoute.distance?.toFixed(0)}</p><p className="text-[9px] lg:text-[10px] text-white/40">km</p></div>
        <div className="text-center"><p className="text-base lg:text-lg font-display font-bold text-white">{Math.round(currentRoute.duration)}</p><p className="text-[9px] lg:text-[10px] text-white/40">min</p></div>
        <div className="text-center"><p className="text-base lg:text-lg font-display font-bold text-amber-500">{routeDetails?.warning_summary?.gradient || 0}</p><p className="text-[9px] lg:text-[10px] text-white/40">Steigung</p></div>
        <div className="text-center"><p className="text-base lg:text-lg font-display font-bold text-amber-500">{routeDetails?.warning_summary?.bend || 0}</p><p className="text-[9px] lg:text-[10px] text-white/40">Kurven</p></div>
      </div>
      {routeDetails?.stress_index && (
        <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: routeDetails.stress_index.score <= 25 ? '#10B981' : routeDetails.stress_index.score <= 50 ? '#F59E0B' : routeDetails.stress_index.score <= 75 ? '#F97316' : '#EF4444' }}>
              {routeDetails.stress_index.score}
            </div>
            <div>
              <p className="text-xs font-medium text-white">{routeDetails.stress_index.label}</p>
              <p className="text-[9px] text-white/40">Fahrzeug-Stress</p>
            </div>
          </div>
          {weatherData?.route_weather_summary && (
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${weatherData.route_weather_summary.overall_risk === 'low' ? 'bg-green-500' : weatherData.route_weather_summary.overall_risk === 'medium' ? 'bg-amber-500' : 'bg-red-500'}`}></div>
              <span className="text-[10px] text-white/40">Wetter</span>
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={`h-full w-full overflow-hidden relative flex flex-col lg:flex-row ${navigationMode ? 'fixed inset-0 z-[99999] bg-black' : ''}`}>
      {/* Mobile Panel Overlay */}
      {panelOpen && (
        <div className={`lg:hidden fixed inset-0 z-[2000] backdrop-blur-xl flex flex-col curvio-panel ${isDark ? 'bg-gray-950/95' : 'bg-white/95'}`}>
          <div className="flex items-center justify-between p-3 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Navigation2 className="w-5 h-5 text-blue-400" />
              <span className="font-semibold text-base text-white">Route planen</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPanelOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10" data-testid="close-panel-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex-1 flex flex-col overflow-hidden">
            {panelContent}
          </div>
        </div>
      )}

      {/* Desktop Left Panel - Glassmorphism (hidden during navigation) */}
      <div className={`${navigationMode ? 'hidden' : 'hidden lg:flex'} w-80 backdrop-blur-xl flex-col flex-shrink-0 overflow-y-auto curvio-panel ${isDark ? 'bg-gray-950/80 border-r border-white/10' : 'bg-white/90 border-r border-gray-200'}`}>
        {panelContent}
      </div>

      {/* SINGLE MAP CONTAINER - flex-1 fills remaining space */}
      <div className="flex-1 relative">
        <Map
          ref={mapRef}
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          onClick={handleMapClick}
          mapStyle={MAP_STYLE}
          terrain={{ source: 'terrainSource', exaggeration: 1.5 }}
          maxPitch={85}
          style={{ width: '100%', height: '100%' }}
        >
          <NavigationControl position="top-right" />
          {mapMarkers}
        </Map>

        {/* Mobile-only controls (hidden during navigation) */}
        <div className={`lg:hidden absolute top-4 left-4 z-[1000] ${navigationMode ? 'hidden' : ''}`}>
          <button onClick={() => setPanelOpen(true)} className={`${GLASS_BUTTON} h-10 px-4 text-sm font-medium rounded-xl flex items-center gap-2`} data-testid="open-panel-btn">
            <Menu className="w-4 h-4" />Route planen
          </button>
        </div>
        <div className="lg:hidden absolute top-4 right-16 z-[1000]">
          <button className={`${GLASS_BUTTON} w-10 h-10 lg:w-9 lg:h-9 rounded-xl flex items-center justify-center`}>
            <LocateFixed className="w-5 h-5 lg:w-4 lg:h-4" />
          </button>
        </div>

        {/* Floating controls (theme, diary, convoy) - hidden during navigation */}
        <div className={`absolute top-16 lg:top-14 right-4 lg:right-2 z-[1000] flex flex-col gap-2 ${navigationMode ? 'hidden' : ''}`}>
          <button
            className={`${GLASS_BUTTON} w-10 h-10 lg:w-9 lg:h-9 rounded-xl flex items-center justify-center`}
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            title={isDark ? 'Tag-Modus' : 'Nacht-Modus'}
          >
            {isDark ? <Sun className="w-5 h-5 lg:w-4 lg:h-4" /> : <Moon className="w-5 h-5 lg:w-4 lg:h-4" />}
          </button>
          <button className={`${GLASS_BUTTON} w-10 h-10 lg:w-9 lg:h-9 rounded-xl flex items-center justify-center`} onClick={() => setShowDiary(true)} title="Reisetagebuch">
            <BookOpen className="w-5 h-5 lg:w-4 lg:h-4" />
          </button>
          <button className={`${GLASS_BUTTON} w-10 h-10 lg:w-9 lg:h-9 rounded-xl flex items-center justify-center`} onClick={() => setShowConvoy(true)} title="Konvoi-Modus">
            <Users className="w-5 h-5 lg:w-4 lg:h-4" />
          </button>
        </div>

        {/* Click-to-add mode indicator */}
        {clickToAddMode && (
          <div className="absolute top-16 lg:top-2 left-1/2 -translate-x-1/2 z-[1000]">
            <div className="bg-purple-500/90 backdrop-blur text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 shadow-lg">
              <MousePointer2 className="w-3 h-3" />Tippe auf Karte
              <button onClick={() => setClickToAddMode(false)} className="hover:bg-white/20 rounded-full p-0.5"><X className="w-3 h-3" /></button>
            </div>
          </div>
        )}

        {/* Fly-Through / Cinematic Preview Stop Button + Speed Control */}
        {(flyThroughActive || cinematicPreview) && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1500] flex flex-col items-center gap-2">
            <button
              onClick={cinematicPreview ? stopCinematicPreview : stopFlyThrough}
              className="bg-red-600/90 backdrop-blur-lg text-white px-6 py-2.5 rounded-full text-sm font-medium flex items-center gap-2 shadow-2xl border border-red-400/30 hover:bg-red-700 transition-colors"
            >
              <Pause className="w-4 h-4" />{cinematicPreview ? 'Preview stoppen' : 'Stop Fly-Through'}
            </button>
            {cinematicPreview && (
              <div className={`backdrop-blur-lg px-4 py-2 rounded-full flex items-center gap-3 border ${isDark ? 'bg-black/70 border-white/10' : 'bg-white/80 border-gray-200'}`}>
                <span className="text-xs">🐢</span>
                <input type="range" min="0.3" max="2" step="0.1"
                  value={previewSpeed}
                  onChange={(e) => setPreviewSpeed(parseFloat(e.target.value))}
                  className="w-24 accent-purple-500"
                />
                <span className="text-xs">🐇</span>
                <span className={`text-xs font-mono w-8 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{previewSpeed.toFixed(1)}x</span>
              </div>
            )}
          </div>
        )}

        {/* Live Traffic Badge on Map */}
        {liveTrafficActive && currentRoute && !navigationMode && !cinematicPreview && (
          <div className="absolute top-4 right-4 z-[1000]">
            <div className={`flex items-center gap-1.5 backdrop-blur-lg px-2.5 py-1.5 rounded-full border ${isDark ? 'bg-black/60 border-green-500/30' : 'bg-white/80 border-green-300'}`}>
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              <span className={`text-[10px] font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>Live Traffic</span>
            </div>
          </div>
        )}

        {/* Route Summary Card - Glass (hidden on mobile when panel open, hidden during navigation) */}
        {routeSummaryCard && !navigationMode && (
          <div className={`absolute bottom-4 left-4 right-4 z-[1000] curvio-panel ${panelOpen ? 'hidden lg:block' : ''}`}>
            {routeSummaryCard}
          </div>
        )}
      </div>

      {/* Navigation Mode Overlay */}
      {navigationMode && currentRoute && (
        <NavigationOverlay
          currentStep={currentNavigationStep}
          nextStep={nextNavigationStep}
          totalSteps={currentRoute?.steps?.length || 0}
          stepIndex={currentStepIndex}
          onPrevStep={prevStep}
          onNextStep={nextStep}
          onStop={stopNavigation}
          startQuery={startQuery}
          endQuery={endQuery}
          route={currentRoute}
          navTheme={navTheme}
          onChangeTheme={handleThemeChange}
          startLocation={startLocation}
          endLocation={endLocation}
          warnings={routeDetails?.warnings || []}
          environmentalZones={environmentalZones}
          scenicSpots={scenicSpots}
        />
      )}

      {/* Route Diary */}
      {showDiary && routes.length > 0 && (
        <RouteDiary routeId={null} isOpen={showDiary} onClose={() => setShowDiary(false)} />
      )}

      {/* Convoy Mode */}
      <ConvoyMode isOpen={showConvoy} onClose={() => setShowConvoy(false)} />
    </div>
  );
};

export default MapView;
